import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import ProEcopreneurPopup from "../components/ProEcopreneurPopup";
import { DailyOpportunities, WhaleTracker, SuperAIFeatures, CryptoPredictions } from "../components/dashboard";
import { 
  TrendingUp, 
  TrendingDown,
  MessageSquare,
  User,
  LogOut,
  RefreshCw,
  BarChart3,
  Gem,
  Flame,
  Crown,
  Star,
  Plus,
  X,
  Sparkles,
  ExternalLink,
  Award,
  Bitcoin,
  Newspaper,
  ChevronRight,
  Home,
  Menu,
  LineChart,
  Zap,
  Globe,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  Lock,
  Unlock,
  PieChart,
  Calendar,
  CreditCard,
  ShoppingCart,
  DollarSign,
  Trophy,
  Target,
  Maximize2,
  Minimize2,
  ShieldCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("token");

// Global axios interceptor: auto-logout on 401 (expired token)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

const LOGO_URL = "/doslions-logo.png";

// Price Accuracy Tracker Badge - shows accuracy % and last update time
const AccuracyBadge = ({ generatedAt, accuracy, label }) => {
  const timeAgo = generatedAt ? (() => {
    const diff = Date.now() - new Date(generatedAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  })() : null;

  return (
    <div className="flex items-center gap-1.5 text-[9px]" data-testid="accuracy-badge">
      <ShieldCheck className="w-3 h-3 text-emerald-500" />
      <span className="text-emerald-400 font-bold">{accuracy}% Accurate</span>
      {timeAgo && (
        <>
          <span className="text-zinc-600">|</span>
          <span className="text-zinc-500">Updated {timeAgo}</span>
        </>
      )}
    </div>
  );
};

// Lazy-loading iframe — only loads TradingView when scrolled into view
// Cuts initial page load by ~40% since TradingView scripts are heavy
const LazyIframe = ({ src, title, className, style, ...props }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // Start loading 200px before visible
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  
  return (
    <div ref={ref} className={className} style={style}>
      {isVisible ? (
        <iframe src={src} title={title} className="w-full h-full border-0" {...props} />
      ) : (
        <div className="w-full h-full bg-zinc-900/50 animate-pulse rounded-lg flex items-center justify-center">
          <span className="text-zinc-600 text-sm">Loading chart...</span>
        </div>
      )}
    </div>
  );
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Pro Ecopreneur Popup state
  const [showProPopup, setShowProPopup] = useState(false);
  const [popupFeature, setPopupFeature] = useState("");
  
  // Subscription status
  const isPro = user?.subscription_tier === "pro" && user?.is_subscribed;
  
  // Function to show Pro Ecopreneur popup
  const showUpgradePopup = (featureName) => {
    setPopupFeature(featureName);
    setShowProPopup(true);
  };
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Market data states
  const [stocks, setStocks] = useState([]);
  const [extendedStocks, setExtendedStocks] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [extendedCommodities, setExtendedCommodities] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [extendedCrypto, setExtendedCrypto] = useState([]);
  const [indices, setIndices] = useState([]);
  
  // News and trending
  const [news, setNews] = useState([]);
  const [weeklyTrending, setWeeklyTrending] = useState(null);
  const [dailyOpportunities, setDailyOpportunities] = useState(null);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const [lionaiPower, setLionaiPower] = useState(null);
  
  // AI Daily Insights (Pro Only - 3 New Sections)
  const [dailyInsights, setDailyInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  
  // AI Crypto Predictions (Pro Only - Daily/Weekly/Monthly)
  const [cryptoPredictions, setCryptoPredictions] = useState(null);
  const [loadingCryptoPredictions, setLoadingCryptoPredictions] = useState(false);
  
  // Sports Betting
  const [sportsGames, setSportsGames] = useState([]);
  const [sportsReport, setSportsReport] = useState(null);
  const [loadingSports, setLoadingSports] = useState(false);
  
  // AI Ultra Accurate Predictions
  const [aiPredictions, setAiPredictions] = useState([]);
  const [aiPredictionsSummary, setAiPredictionsSummary] = useState(null);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  
  // AI Sports Picks (Daily/Weekly/Monthly)
  const [sportsAiPicks, setSportsAiPicks] = useState(null);
  const [loadingSportsAiPicks, setLoadingSportsAiPicks] = useState(false);
  
  // Sports Results Table (Last 7 days)
  const [sportsResults, setSportsResults] = useState(null);
  const [loadingSportsResults, setLoadingSportsResults] = useState(false);
  
  // Auto-refresh timer state
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [nextRefreshIn, setNextRefreshIn] = useState(180); // 3 minutes in seconds
  
  // Watchlist
  const [watchlist, setWatchlist] = useState([]);
  
  // Chart View State (TradingView)
  const [chartSymbol, setChartSymbol] = useState("AAPL");
  const [chartInterval, setChartInterval] = useState("D");
  const [showChartSearch, setShowChartSearch] = useState(false);
  const [chartSearchQuery, setChartSearchQuery] = useState("");
  const [chartSearchResults, setChartSearchResults] = useState([]);
  const [chartSearchLoading, setChartSearchLoading] = useState(false);
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const [recentChartSymbols, setRecentChartSymbols] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('recentChartSymbols') || '["AAPL", "BTCUSD", "EURUSD", "GOLD"]');
    } catch { return ["AAPL", "BTCUSD", "EURUSD", "GOLD"]; }
  });
  
  // Draggable fullscreen controls positions
  const [controlsPosition, setControlsPosition] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('chartControlsPosition') || '{"x": null, "y": null}');
    } catch { return { x: null, y: null }; }
  });
  const [searchPosition, setSearchPosition] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('chartSearchPosition') || '{"x": null, "y": null}');
    } catch { return { x: null, y: null }; }
  });
  const [isDragging, setIsDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);
  const [loadingTrending, setLoadingTrending] = useState(false);

  // SUPER AI FEATURES State
  const [earningsWhisper, setEarningsWhisper] = useState(null);
  const [socialSentiment, setSocialSentiment] = useState(null);
  const [economicCalendar, setEconomicCalendar] = useState(null);
  const [whaleTracker, setWhaleTracker] = useState(null);
  const [hotPicks, setHotPicks] = useState(null);
  const [selectedHotPick, setSelectedHotPick] = useState(null);
  const [topMovers, setTopMovers] = useState(null);
  const [selectedMover, setSelectedMover] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${getToken()}` }};
  const wsRef = useRef(null);

  // WebSocket: Live price streaming — replaces 3-min polling
  useEffect(() => {
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsProtocol}//${window.location.host}/ws/prices`;
    
    const connectWs = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "price_update") {
              // Update market data from WebSocket stream
              if (data.stocks?.length) setStocks(prev => data.stocks.length > 0 ? data.stocks : prev);
              if (data.crypto?.length) setCrypto(prev => data.crypto.length > 0 ? data.crypto : prev);
              if (data.commodities?.length) setCommodities(prev => data.commodities.length > 0 ? data.commodities : prev);
              setLastRefresh(new Date());
            }
          } catch {}
        };
        
        ws.onclose = () => {
          // Auto-reconnect after 5 seconds
          setTimeout(connectWs, 5000);
        };
        
        ws.onerror = () => ws.close();
      } catch {}
    };
    
    connectWs();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, []);

  // ENHANCED YAHOO-STYLE SEARCH
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setSearchLoading(true);
    setShowSearchResults(true);
    
    try {
      // Use enhanced suggestions endpoint for Yahoo-style search
      const res = await axios.get(`${API}/search/suggestions/${query.toUpperCase()}`, authHeaders);
      setSearchResults(res.data.suggestions || []);
    } catch (error) {
      // Fallback to regular search
      try {
        const res = await axios.get(`${API}/search/${query.toUpperCase()}`, authHeaders);
        setSearchResults(res.data.results || []);
      } catch {
        setSearchResults([]);
      }
    } finally {
      setSearchLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: run search once per query change

  // Debounced search - faster response
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      }
    }, 200); // Reduced from 300ms for faster response
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // TradingView Symbol Search - Uses TradingView's comprehensive symbol database
  const POPULAR_SYMBOLS = {
    stocks: [
      { symbol: 'AAPL', name: 'Apple Inc', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'MSFT', name: 'Microsoft Corporation', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'GOOGL', name: 'Alphabet Inc', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'AMZN', name: 'Amazon.com Inc', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'NVDA', name: 'NVIDIA Corporation', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'TSLA', name: 'Tesla Inc', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'META', name: 'Meta Platforms Inc', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'JPM', name: 'JPMorgan Chase & Co', exchange: 'NYSE', type: 'stock' },
      { symbol: 'V', name: 'Visa Inc', exchange: 'NYSE', type: 'stock' },
      { symbol: 'WMT', name: 'Walmart Inc', exchange: 'NYSE', type: 'stock' },
      { symbol: 'DIS', name: 'Walt Disney Co', exchange: 'NYSE', type: 'stock' },
      { symbol: 'NFLX', name: 'Netflix Inc', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'AMD', name: 'Advanced Micro Devices', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'INTC', name: 'Intel Corporation', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'BA', name: 'Boeing Co', exchange: 'NYSE', type: 'stock' },
      { symbol: 'GS', name: 'Goldman Sachs', exchange: 'NYSE', type: 'stock' },
      { symbol: 'KO', name: 'Coca-Cola Co', exchange: 'NYSE', type: 'stock' },
      { symbol: 'PEP', name: 'PepsiCo Inc', exchange: 'NASDAQ', type: 'stock' },
      { symbol: 'MCD', name: "McDonald's Corp", exchange: 'NYSE', type: 'stock' },
      { symbol: 'NKE', name: 'Nike Inc', exchange: 'NYSE', type: 'stock' },
    ],
    crypto: [
      { symbol: 'BTCUSD', name: 'Bitcoin / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'ETHUSD', name: 'Ethereum / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'SOLUSD', name: 'Solana / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'BNBUSD', name: 'Binance Coin / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'XRPUSD', name: 'Ripple / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'ADAUSD', name: 'Cardano / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'DOGEUSD', name: 'Dogecoin / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'DOTUSD', name: 'Polkadot / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'AVAXUSD', name: 'Avalanche / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'SHIBUSD', name: 'Shiba Inu / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'LINKUSD', name: 'Chainlink / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'MATICUSD', name: 'Polygon / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'LTCUSD', name: 'Litecoin / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'UNIUSD', name: 'Uniswap / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
      { symbol: 'APTUSD', name: 'Aptos / US Dollar', exchange: 'CRYPTO', type: 'crypto' },
    ],
    forex: [
      { symbol: 'EURUSD', name: 'Euro / US Dollar', exchange: 'FX', type: 'forex' },
      { symbol: 'GBPUSD', name: 'British Pound / US Dollar', exchange: 'FX', type: 'forex' },
      { symbol: 'USDJPY', name: 'US Dollar / Japanese Yen', exchange: 'FX', type: 'forex' },
      { symbol: 'AUDUSD', name: 'Australian Dollar / US Dollar', exchange: 'FX', type: 'forex' },
      { symbol: 'USDCAD', name: 'US Dollar / Canadian Dollar', exchange: 'FX', type: 'forex' },
      { symbol: 'USDCHF', name: 'US Dollar / Swiss Franc', exchange: 'FX', type: 'forex' },
      { symbol: 'NZDUSD', name: 'New Zealand Dollar / US Dollar', exchange: 'FX', type: 'forex' },
      { symbol: 'EURGBP', name: 'Euro / British Pound', exchange: 'FX', type: 'forex' },
      { symbol: 'EURJPY', name: 'Euro / Japanese Yen', exchange: 'FX', type: 'forex' },
      { symbol: 'GBPJPY', name: 'British Pound / Japanese Yen', exchange: 'FX', type: 'forex' },
    ],
    commodities: [
      { symbol: 'XAUUSD', name: 'Gold / US Dollar', exchange: 'COMMODITIES', type: 'commodity' },
      { symbol: 'XAGUSD', name: 'Silver / US Dollar', exchange: 'COMMODITIES', type: 'commodity' },
      { symbol: 'USOIL', name: 'Crude Oil WTI', exchange: 'COMMODITIES', type: 'commodity' },
      { symbol: 'UKOIL', name: 'Brent Crude Oil', exchange: 'COMMODITIES', type: 'commodity' },
      { symbol: 'NATGAS', name: 'Natural Gas', exchange: 'COMMODITIES', type: 'commodity' },
      { symbol: 'COPPER', name: 'Copper', exchange: 'COMMODITIES', type: 'commodity' },
      { symbol: 'PLATINUM', name: 'Platinum', exchange: 'COMMODITIES', type: 'commodity' },
      { symbol: 'PALLADIUM', name: 'Palladium', exchange: 'COMMODITIES', type: 'commodity' },
    ],
    indices: [
      { symbol: 'SPX', name: 'S&P 500 Index', exchange: 'SP', type: 'index' },
      { symbol: 'DJI', name: 'Dow Jones Industrial Average', exchange: 'DJ', type: 'index' },
      { symbol: 'IXIC', name: 'NASDAQ Composite', exchange: 'NASDAQ', type: 'index' },
      { symbol: 'NDX', name: 'NASDAQ 100', exchange: 'NASDAQ', type: 'index' },
      { symbol: 'VIX', name: 'Volatility Index', exchange: 'TVC', type: 'index' },
      { symbol: 'DXY', name: 'US Dollar Index', exchange: 'TVC', type: 'index' },
      { symbol: 'FTSE', name: 'FTSE 100', exchange: 'TVC', type: 'index' },
      { symbol: 'DAX', name: 'DAX 40', exchange: 'XETR', type: 'index' },
      { symbol: 'NIKKEI', name: 'Nikkei 225', exchange: 'TVC', type: 'index' },
    ],
  };

  const handleChartSearch = useCallback((query) => {
    if (!query || query.length < 1) {
      setChartSearchResults([]);
      return;
    }
    
    setChartSearchLoading(true);
    const upperQuery = query.toUpperCase();
    
    // Search across all categories
    const allSymbols = [
      ...POPULAR_SYMBOLS.stocks,
      ...POPULAR_SYMBOLS.crypto,
      ...POPULAR_SYMBOLS.forex,
      ...POPULAR_SYMBOLS.commodities,
      ...POPULAR_SYMBOLS.indices,
    ];
    
    const results = allSymbols.filter(item => 
      item.symbol.includes(upperQuery) || 
      item.name.toUpperCase().includes(upperQuery)
    ).slice(0, 15); // Limit to 15 results
    
    setChartSearchResults(results);
    setChartSearchLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- static symbol list

  // Debounced chart search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (chartSearchQuery) {
        handleChartSearch(chartSearchQuery);
      } else {
        setChartSearchResults([]);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [chartSearchQuery, handleChartSearch]);

  // Save recent chart symbols to localStorage
  const selectChartSymbol = (symbol) => {
    setChartSymbol(symbol);
    setShowChartSearch(false);
    setChartSearchQuery("");
    setChartSearchResults([]);
    
    // Update recent symbols
    const newRecent = [symbol, ...recentChartSymbols.filter(s => s !== symbol)].slice(0, 8);
    setRecentChartSymbols(newRecent);
    localStorage.setItem('recentChartSymbols', JSON.stringify(newRecent));
  };


  // Fetch Daily AI Opportunities
  const fetchDailyOpportunities = async () => {
    if (!isPro) return;
    setLoadingOpportunities(true);
    try {
      const res = await axios.get(`${API}/opportunities/daily`, authHeaders);
      if (res.data.success) {
        setDailyOpportunities(res.data);
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error);
    } finally {
      setLoadingOpportunities(false);
    }
  };

  // Refresh opportunities manually
  const refreshOpportunities = async () => {
    setLoadingOpportunities(true);
    try {
      const res = await axios.post(`${API}/opportunities/refresh`, {}, authHeaders);
      if (res.data.success) {
        setDailyOpportunities(res.data);
        toast.success("🔥 Opportunities updated!");
      }
    } catch (error) {
      toast.error("Failed to refresh opportunities");
    } finally {
      setLoadingOpportunities(false);
    }
  };

  // Fetch AI Daily Insights - PRO ONLY (Updates once per day at 8 AM EST)
  const fetchDailyInsights = async () => {
    if (!isPro) return;
    setLoadingInsights(true);
    try {
      const res = await axios.get(`${API}/insights/daily`, authHeaders);
      if (res.data.success) {
        setDailyInsights(res.data);
      }
    } catch (error) {
      console.error("Error fetching daily insights:", error);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Fetch AI Crypto Predictions (Pro Only)
  const fetchCryptoPredictions = async () => {
    if (!isPro) return;
    setLoadingCryptoPredictions(true);
    try {
      const res = await axios.get(`${API}/ai/crypto-predictions`, authHeaders);
      if (res.data.success) {
        setCryptoPredictions(res.data);
      }
    } catch (error) {
      console.error("Error fetching crypto predictions:", error);
    } finally {
      setLoadingCryptoPredictions(false);
    }
  };

  // Fetch SUPER AI Features (Pro Only)
  const fetchEarningsWhisper = async () => {
    if (!isPro) return;
    try {
      const res = await axios.get(`${API}/ai/earnings-whisper`, authHeaders);
      if (res.data.success) setEarningsWhisper(res.data);
    } catch (error) {
      console.error("Error fetching earnings whisper:", error);
    }
  };

  const fetchSocialSentiment = async () => {
    if (!isPro) return;
    try {
      const res = await axios.get(`${API}/ai/social-sentiment`, authHeaders);
      if (res.data.success) setSocialSentiment(res.data);
    } catch (error) {
      console.error("Error fetching social sentiment:", error);
    }
  };

  const fetchEconomicCalendar = async () => {
    if (!isPro) return;
    try {
      const res = await axios.get(`${API}/ai/economic-calendar`, authHeaders);
      if (res.data.success) setEconomicCalendar(res.data);
    } catch (error) {
      console.error("Error fetching economic calendar:", error);
    }
  };

  const fetchWhaleTracker = async () => {
    if (!isPro) return;
    try {
      const res = await axios.get(`${API}/ai/whale-tracker`, authHeaders);
      if (res.data.success) setWhaleTracker(res.data);
    } catch (error) {
      console.error("Error fetching whale tracker:", error);
    }
  };

  const fetchHotPicks = async () => {
    try {
      const res = await axios.get(`${API}/ai/hot-picks`, authHeaders);
      if (res.data.success) setHotPicks(res.data);
    } catch (error) {
      console.error("Error fetching hot picks:", error);
    }
  };

  const fetchTopMovers = async () => {
    try {
      const res = await axios.get(`${API}/ai/top-movers`, authHeaders);
      if (res.data.success) setTopMovers(res.data);
    } catch (error) {
      console.error("Error fetching top movers:", error);
    }
  };

  const fetchMarketData = async () => {
    try {
      // Fetch indices for all users
      const indicesRes = await axios.get(`${API}/indices`).catch(() => null);
      if (indicesRes?.data) {
        setIndices(indicesRes.data.indices || []);
      }
      
      // Free users get basic data, Pro users get extended
      if (isPro) {
        const [stocksRes, commoditiesRes, cryptoRes, watchlistRes] = await Promise.all([
          axios.get(`${API}/stocks/extended`, authHeaders).catch(() => null),
          axios.get(`${API}/commodities/extended`, authHeaders).catch(() => null),
          axios.get(`${API}/crypto/extended`, authHeaders).catch(() => null),
          axios.get(`${API}/watchlist`, authHeaders).catch(() => ({ data: { watchlist: [] }}))
        ]);
        
        if (stocksRes?.data) {
          setStocks(stocksRes.data.regular_stocks || []);
          setExtendedStocks(stocksRes.data.extended_stocks || []);
        }
        if (commoditiesRes?.data) {
          setCommodities(commoditiesRes.data.regular_commodities || []);
          setExtendedCommodities(commoditiesRes.data.extended_commodities || []);
        }
        if (cryptoRes?.data) {
          setCrypto(cryptoRes.data.regular_crypto || []);
          setExtendedCrypto(cryptoRes.data.extended_crypto || []);
        }
        setWatchlist(watchlistRes?.data?.watchlist || []);
      } else {
        // Free users - basic data only
        const [stocksRes, commoditiesRes, cryptoRes, watchlistRes] = await Promise.all([
          axios.get(`${API}/stocks`),
          axios.get(`${API}/commodities`),
          axios.get(`${API}/crypto`),
          axios.get(`${API}/watchlist`, authHeaders).catch(() => ({ data: { watchlist: [] }}))
        ]);
        
        setStocks(stocksRes.data.stocks || []);
        setCommodities(commoditiesRes.data.commodities || []);
        setCrypto(cryptoRes.data.crypto || []);
        setWatchlist(watchlistRes?.data?.watchlist || []);
        setExtendedStocks([]);
        setExtendedCommodities([]);
        setExtendedCrypto([]);
      }
      
    } catch (error) {
      console.error("Error fetching market data:", error);
      // Fallback to basic endpoints for all users
      try {
        const [stocksRes, commoditiesRes, cryptoRes] = await Promise.all([
          axios.get(`${API}/stocks`),
          axios.get(`${API}/commodities`),
          axios.get(`${API}/crypto`)
        ]);
        setStocks(stocksRes.data.stocks || []);
        setCommodities(commoditiesRes.data.commodities || []);
        setCrypto(cryptoRes.data.crypto || []);
      } catch (e) {
        toast.error("Failed to fetch market data");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch Sports Betting Data - PRO ONLY (Real-time from ESPN)
  const fetchSportsData = async () => {
    if (!isPro) return; // Sports is Pro only
    setLoadingSports(true);
    try {
      // Use new live endpoint for real-time games
      const [liveRes, reportRes] = await Promise.all([
        axios.get(`${API}/sports/live`, authHeaders),
        axios.get(`${API}/sports/report`, authHeaders)
      ]);
      setSportsGames(liveRes.data.games || []);
      setSportsReport(reportRes.data.report || null);
    } catch (error) {
      console.error("Error fetching sports data:", error);
      if (error.response?.status === 403) {
        console.log("Sports requires Pro subscription");
      }
    } finally {
      setLoadingSports(false);
    }
  };

  // Fetch AI Ultra Accurate Predictions - PRO ONLY
  const fetchAiPredictions = async () => {
    if (!isPro) return;
    setLoadingPredictions(true);
    try {
      const res = await axios.get(`${API}/sports/ai-predictions`, authHeaders);
      setAiPredictions(res.data.predictions || []);
      setAiPredictionsSummary(res.data.summary || null);
    } catch (error) {
      console.error("Error fetching AI predictions:", error);
    } finally {
      setLoadingPredictions(false);
    }
  };

  // Fetch AI Sports Picks (Daily/Weekly/Monthly) - PRO ONLY
  const fetchSportsAiPicks = async () => {
    if (!isPro) return;
    setLoadingSportsAiPicks(true);
    try {
      const res = await axios.get(`${API}/sports/ai-picks`, authHeaders);
      if (res.data.success) {
        setSportsAiPicks(res.data);
      }
    } catch (error) {
      console.error("Error fetching sports AI picks:", error);
    } finally {
      setLoadingSportsAiPicks(false);
    }
  };

  // Fetch Sports Results (Last 7 Days) - PRO ONLY
  const fetchSportsResults = async () => {
    if (!isPro) return;
    setLoadingSportsResults(true);
    try {
      const res = await axios.get(`${API}/sports/results`, authHeaders);
      if (res.data.success) {
        setSportsResults(res.data);
      }
    } catch (error) {
      console.error("Error fetching sports results:", error);
    } finally {
      setLoadingSportsResults(false);
    }
  };

  const fetchNews = async () => {
    if (!isPro) return; // News is Pro only
    setLoadingNews(true);
    try {
      const res = await axios.get(`${API}/news`, authHeaders);
      setNews(res.data.news || []);
    } catch (error) {
      if (error.response?.status === 403) {
        // Expected for free users
      } else {
        console.error("Error fetching news:", error);
      }
    } finally {
      setLoadingNews(false);
    }
  };

  const fetchWeeklyTrending = async () => {
    if (!isPro) {
      toast.error("Upgrade to Pro Ecopreneur to access MEGA Weekly Trending");
      return;
    }
    setLoadingTrending(true);
    try {
      const res = await axios.get(`${API}/trending/mega-weekly`, authHeaders);
      setWeeklyTrending(res.data);
      if (res.data.success) {
        toast.success("🔥 MEGA Weekly Trending loaded!");
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Upgrade to Pro Ecopreneur to access MEGA Weekly Trending");
      } else {
        toast.error("Failed to load weekly trending");
      }
    } finally {
      setLoadingTrending(false);
    }
  };

  // Fetch LIONAI Power status
  const fetchLionaiPower = async () => {
    try {
      const res = await axios.get(`${API}/lionai/power`, authHeaders);
      setLionaiPower(res.data);
    } catch (error) {
      console.error("Error fetching LIONAI Power:", error);
    }
  };

  // Purchase LIONAI Power recharge
  const purchaseRecharge = async () => {
    try {
      const res = await axios.post(`${API}/lionai/recharge`, {}, authHeaders);
      if (res.data.success) {
        toast.success(`🎉 ${res.data.message}`);
        fetchLionaiPower(); // Refresh power status
      }
    } catch (error) {
      toast.error("Failed to process recharge - contact support");
    }
  };

  useEffect(() => {
    // Load market data first (fast, cached)
    fetchMarketData();
    setLastRefresh(new Date());
    
    if (isPro) {
      // Stagger AI calls to avoid overwhelming the backend on cold start
      // Group 1: Core AI sections (immediate)
      fetchHotPicks();
      fetchTopMovers();
      fetchWhaleTracker();
      
      // Group 2: Insights (after 1s)
      setTimeout(() => {
        fetchDailyInsights();
        fetchEarningsWhisper();
        fetchSocialSentiment();
      }, 1000);
      
      // Group 3: Predictions (after 2.5s)
      setTimeout(() => {
        fetchDailyOpportunities();
        fetchCryptoPredictions();
        fetchEconomicCalendar();
      }, 2500);
      
      // Group 4: News & Weekly (after 4s)
      setTimeout(() => {
        fetchNews();
        fetchWeeklyTrending();
        fetchLionaiPower();
      }, 4000);
      
      // Group 5: Sports (after 5s)
      setTimeout(() => {
        fetchSportsData();
        fetchAiPredictions();
        fetchSportsAiPicks();
        fetchSportsResults();
      }, 5000);
    }
    
    // Auto-refresh market data every 3 minutes
    const marketInterval = setInterval(() => {
      fetchMarketData();
      setLastRefresh(new Date());
      setNextRefreshIn(180);
    }, 3 * 60 * 1000);
    
    // Auto-refresh AI sections every 15 minutes to keep data fresh
    let aiInterval;
    let sportsInterval;
    let predictionsInterval;
    if (isPro) {
      aiInterval = setInterval(() => {
        fetchHotPicks();
        fetchTopMovers();
        fetchWhaleTracker();
        fetchEarningsWhisper();
        fetchSocialSentiment();
        fetchEconomicCalendar();
        fetchDailyInsights();
        fetchDailyOpportunities();
        fetchCryptoPredictions();
      }, 15 * 60 * 1000); // 15 min
      sportsInterval = setInterval(fetchSportsData, 2 * 60 * 1000);
      predictionsInterval = setInterval(fetchAiPredictions, 30 * 60 * 1000);
    }
    
    return () => {
      clearInterval(marketInterval);
      if (aiInterval) clearInterval(aiInterval);
      if (sportsInterval) clearInterval(sportsInterval);
      if (predictionsInterval) clearInterval(predictionsInterval);
    };
  }, [isPro]); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only data fetching, isPro gates Pro features

  // Countdown timer for next refresh (every second)
  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setNextRefreshIn(prev => prev > 0 ? prev - 1 : 180);
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, []);

  // ESC key to close fullscreen chart
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && chartFullscreen) {
        setChartFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [chartFullscreen]);

  // Drag handlers for fullscreen controls
  const handleDragStart = (e, controlType) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setIsDragging(controlType);
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    if (isDragging === 'controls') {
      const newPos = { x: newX, y: newY };
      setControlsPosition(newPos);
      localStorage.setItem('chartControlsPosition', JSON.stringify(newPos));
    } else if (isDragging === 'search') {
      const newPos = { x: newX, y: newY };
      setSearchPosition(newPos);
      localStorage.setItem('chartSearchPosition', JSON.stringify(newPos));
    }
  }, [isDragging, dragOffset]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(null);
  }, []);

  // Add mouse move/up listeners when dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Reset positions when exiting fullscreen
  const resetControlPositions = () => {
    setControlsPosition({ x: null, y: null });
    setSearchPosition({ x: null, y: null });
    localStorage.removeItem('chartControlsPosition');
    localStorage.removeItem('chartSearchPosition');
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMarketData();
    if (isPro) {
      fetchSportsData();
      fetchAiPredictions();
      fetchSportsAiPicks();
      fetchSportsResults();
      fetchNews();
      fetchDailyOpportunities();
      fetchDailyInsights();
      fetchCryptoPredictions();
      fetchWeeklyTrending();
      fetchEarningsWhisper();
      fetchSocialSentiment();
      fetchEconomicCalendar();
      fetchWhaleTracker();
      fetchHotPicks();
      fetchTopMovers();
    }
    toast.success("Data refreshed");
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const addToWatchlist = async (symbol, type) => {
    try {
      await axios.post(`${API}/watchlist`, { symbol, type }, authHeaders);
      toast.success(`${symbol} added to watchlist`);
      fetchMarketData();
    } catch (error) {
      toast.error("Failed to add to watchlist");
    }
  };

  const removeFromWatchlist = async (symbol) => {
    try {
      await axios.delete(`${API}/watchlist/${symbol}`, authHeaders);
      toast.success(`${symbol} removed from watchlist`);
      fetchMarketData();
    } catch (error) {
      toast.error("Failed to remove from watchlist");
    }
  };

  const isInWatchlist = (symbol) => watchlist.some(item => item.symbol === symbol);
  
  const isPositive = (change) => {
    return change && (change.startsWith('+') || (!change.startsWith('-') && parseFloat(change) > 0));
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch {
      return "";
    }
  };

  // Sidebar Navigation Component
  const Sidebar = () => (
    <aside className={`fixed left-0 top-0 h-full bg-black border-r border-zinc-800 z-40 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
      <div className="flex flex-col h-full">
        {/* Logo - Clickable to Dashboard */}
        <div className="p-4 border-b border-zinc-800">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate("/dashboard")}
            data-testid="logo-home-link"
          >
            <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
            {sidebarOpen && (
              <div>
                <span className="font-bold text-lg text-amber-500" style={{ fontFamily: 'Barlow Condensed' }}>ECOLIONS</span>
                <span className="font-bold text-lg text-white ml-1" style={{ fontFamily: 'Barlow Condensed' }}>AI</span>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 p-4 space-y-2">
          <button 
            onClick={() => setActiveTab("overview")}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === "overview" ? 'bg-amber-500/20 text-amber-500' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
            data-testid="nav-overview"
          >
            <BarChart3 className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Dashboard</span>}
          </button>
          
          <button 
            onClick={() => setActiveTab("news")}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === "news" ? 'bg-amber-500/20 text-amber-500' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
            data-testid="nav-news"
          >
            <Newspaper className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Market News</span>}
          </button>
          
          <button 
            onClick={() => setActiveTab("trending")}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === "trending" ? 'bg-amber-500/20 text-amber-500' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
            data-testid="nav-trending"
          >
            <Zap className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && (
              <>
                <span className="font-medium">Weekly Trending</span>
                <Crown className="w-3 h-3 text-amber-500 ml-auto" />
              </>
            )}
          </button>
          
          {/* Show Weekly Trending locked for free users */}
          {!isPro && sidebarOpen && (
            <div className="ml-8 text-[10px] text-zinc-600 -mt-1">
              Pro Ecopreneur only
            </div>
          )}
          
          <button 
            onClick={() => setActiveTab("sports")}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === "sports" ? 'bg-emerald-500/20 text-emerald-500' : 'text-zinc-400 hover:text-emerald-500 hover:bg-zinc-800'}`}
            data-testid="nav-sports"
          >
            <Trophy className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && (
              <>
                <span className="font-medium">Sports AI</span>
                <Crown className="w-3 h-3 text-amber-500 ml-auto" />
              </>
            )}
          </button>
          
          {/* Show Sports locked for free users */}
          {!isPro && sidebarOpen && (
            <div className="ml-8 text-[10px] text-zinc-600 -mt-1">
              Pro Ecopreneur only
            </div>
          )}
          
          {/* LIONAI Chat - temporarily disabled for cost optimization */}
          
          <button 
            onClick={() => navigate("/markets")}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:text-emerald-500 hover:bg-zinc-800 transition-colors"
            data-testid="nav-markets"
          >
            <DollarSign className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium text-base">Market Prices</span>}
          </button>
          
          <button 
            onClick={() => navigate("/portfolio")}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:text-amber-500 hover:bg-zinc-800 transition-colors"
            data-testid="nav-portfolio"
          >
            <PieChart className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && (
              <>
                <span className="font-medium text-base">My Portfolio</span>
                {isPro && <Crown className="w-3 h-3 text-amber-500 ml-auto" />}
              </>
            )}
          </button>

          <div className="pt-4 border-t border-zinc-800 mt-4">
            <a 
              href="/"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
              data-testid="nav-home"
            >
              <Home className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">Home</span>}
            </a>
            
            <a 
              href="https://www.doslions.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:text-amber-500 hover:bg-zinc-800 transition-colors"
              data-testid="nav-doslions"
            >
              <Globe className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <>
                  <span className="font-medium">doslions.com</span>
                  <ExternalLink className="w-3 h-3 ml-auto" />
                </>
              )}
            </a>
          </div>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-500 font-bold">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
            )}
          </div>
          {sidebarOpen && (
            <Button 
              onClick={handleLogout}
              variant="ghost"
              className="w-full mt-3 text-zinc-400 hover:text-red-500 justify-start"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          )}
        </div>
      </div>
    </aside>
  );

  // Market Card Component
  const MarketCard = ({ item, type, extended = false }) => (
    <div 
      className={`bg-[#111111] border ${extended ? 'border-purple-500/30' : 'border-zinc-800'} p-4 hover:border-amber-500/50 transition-colors group relative`}
      data-testid={`market-card-${item.symbol}`}
    >
      <button
        onClick={() => isInWatchlist(item.symbol) 
          ? removeFromWatchlist(item.symbol) 
          : addToWatchlist(item.symbol, type)
        }
        className={`absolute top-2 right-2 transition-all ${
          isInWatchlist(item.symbol) 
            ? 'text-amber-500' 
            : 'text-zinc-600 opacity-0 group-hover:opacity-100'
        }`}
        data-testid={`watchlist-btn-${item.symbol}`}
      >
        {isInWatchlist(item.symbol) ? <Star className="w-4 h-4 fill-amber-500" /> : <Plus className="w-4 h-4" />}
      </button>
      
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-zinc-500 tracking-wider">{item.symbol}</span>
        {isPositive(item.change_percent) ? (
          <ArrowUpRight className="w-4 h-4 text-emerald-500" />
        ) : (
          <ArrowDownRight className="w-4 h-4 text-red-500" />
        )}
      </div>
      <p className="text-sm text-zinc-400 truncate mb-1">{item.name}</p>
      <p className="text-xl font-bold font-data">${item.price}</p>
      <p className={`text-sm font-data ${isPositive(item.change_percent) ? 'text-emerald-500' : 'text-red-500'}`}>
        {item.change_percent}
      </p>
      {extended && item.category && (
        <span className="inline-block mt-2 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">
          {item.category}
        </span>
      )}
    </div>
  );

  // News Item Component
  const NewsItem = ({ article }) => (
    <a 
      href={article.link} 
      target="_blank" 
      rel="noopener noreferrer"
      className="block bg-[#111111] border border-zinc-800 p-4 hover:border-amber-500/30 transition-colors"
      data-testid={`news-item-${article.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-amber-500/10 flex items-center justify-center flex-shrink-0">
          <Newspaper className="w-5 h-5 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-white line-clamp-2 mb-1">{article.title}</h3>
          <p className="text-sm text-zinc-500 line-clamp-2 mb-2">{article.summary?.replace(/<[^>]*>/g, '')}</p>
          <div className="flex items-center gap-3 text-xs text-zinc-600">
            <span className="text-amber-500">{article.source}</span>
            <span>{formatTimeAgo(article.published)}</span>
          </div>
        </div>
        <ExternalLink className="w-4 h-4 text-zinc-600 flex-shrink-0" />
      </div>
    </a>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-xl border-b border-zinc-800">
          <div className="px-6 py-4 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 text-zinc-400 hover:text-white transition-colors"
                  data-testid="toggle-sidebar"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                    Welcome back, <span className="text-amber-500">{user?.name}</span>
                  </h1>
                  <p className="text-sm text-zinc-500">Your personalized market dashboard</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Button 
                  onClick={handleRefresh}
                  disabled={refreshing}
                  variant="outline"
                  className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                  data-testid="refresh-all-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {isPro ? (
                  <span className="flex items-center gap-1 px-3 py-1 bg-amber-500/20 text-amber-500 text-sm font-bold rounded">
                    <Crown className="w-4 h-4" /> PRO ECOPRENEUR
                  </span>
                ) : (
                  <Button 
                    onClick={() => navigate("/subscription")}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                    data-testid="upgrade-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" /> Upgrade to Pro
                  </Button>
                )}
              </div>
            </div>
            
          </div>
        </header>

        {/* Click outside to close chart search */}
        {showChartSearch && (
          <div 
            className="fixed inset-0 z-30" 
            onClick={() => { setShowChartSearch(false); setChartSearchQuery(""); setChartSearchResults([]); }}
          />
        )}

        {/* Content Area */}
        <div className="p-6">
          {activeTab === "overview" && (
            <div className="space-y-8">
              {/* Pro Upgrade Banner for Free Users */}
              {!isPro && (
                <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/30 p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-500/20 flex items-center justify-center">
                      <Crown className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-amber-500">Unlock Pro Ecopreneur Features</h3>
                      <p className="text-sm text-zinc-400">Get Trending Stocks, Market News, Weekly AI Picks & more</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate("/subscription")}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                    data-testid="upgrade-banner-btn"
                  >
                    $4.99 First Month <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
              
              {/* Watchlist Quick View */}
              {watchlist.length > 0 && (
                <section>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                      <Star className="w-5 h-5 text-amber-500" />
                      YOUR WATCHLIST
                    </h2>
                    <span className="text-sm text-zinc-500">{watchlist.length} items</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {watchlist.map((item) => (
                      <div key={item.symbol} className="bg-[#111111] border border-amber-500/30 p-3 relative group">
                        <button
                          onClick={() => removeFromWatchlist(item.symbol)}
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-500 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <p className="text-xs text-amber-500 font-bold">{item.symbol}</p>
                        {item.price_data && (
                          <>
                            <p className="text-lg font-bold font-data">${item.price_data.price}</p>
                            <p className={`text-xs font-data ${isPositive(item.price_data.change_percent) ? 'text-emerald-500' : 'text-red-500'}`}>
                              {item.price_data.change_percent}
                            </p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Quick Access to Market Prices */}
              <button
                onClick={() => navigate("/markets")}
                className="w-full bg-emerald-500/10 hover:bg-emerald-500/20 border-2 border-emerald-500 p-4 flex items-center justify-between transition-all group"
                data-testid="quick-markets-btn"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-bold text-emerald-500" style={{ fontFamily: 'Barlow Condensed' }}>
                      VIEW ALL MARKET PRICES
                    </h3>
                    <p className="text-sm text-zinc-400">Stocks, Indices, Commodities, Crypto - Real-time data</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-emerald-500">
                  <span className="text-sm font-bold group-hover:underline">Open Markets</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>

              {/* 🚀 FREE MARKET INSIGHTS - TradingView LIVE Data */}
              <section className="bg-gradient-to-r from-amber-500/5 via-transparent to-emerald-500/5 border border-amber-500/20 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-bold flex items-center gap-2 text-amber-500" style={{ fontFamily: 'Barlow Condensed' }}>
                    <Zap className="w-4 h-4" />
                    FREE MARKET INSIGHTS
                    <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full ml-2 animate-pulse">LIVE</span>
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zinc-500">
                      Last: {lastRefresh?.toLocaleTimeString() || 'N/A'}
                    </span>
                    <span className="text-[10px] text-emerald-500">
                      Next: {Math.floor(nextRefreshIn / 60)}:{(nextRefreshIn % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                </div>
                
                {/* TradingView Market Overview Widget - LAZY LOADED for speed */}
                <div className="mb-4 rounded overflow-hidden border border-zinc-800">
                  <LazyIframe 
                    src="https://s.tradingview.com/embed-widget/market-overview/?locale=en#%7B%22colorTheme%22%3A%22dark%22%2C%22dateRange%22%3A%221D%22%2C%22showChart%22%3Atrue%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22400%22%2C%22largeChartUrl%22%3A%22%22%2C%22isTransparent%22%3Atrue%2C%22showSymbolLogo%22%3Atrue%2C%22showFloatingTooltip%22%3Afalse%2C%22plotLineColorGrowing%22%3A%22rgba%2834%2C%20197%2C%2094%2C%201%29%22%2C%22plotLineColorFalling%22%3A%22rgba%28239%2C%2068%2C%2068%2C%201%29%22%2C%22gridLineColor%22%3A%22rgba%2839%2C%2039%2C%2042%2C%201%29%22%2C%22scaleFontColor%22%3A%22rgba%28161%2C%20161%2C%20170%2C%201%29%22%2C%22belowLineFillColorGrowing%22%3A%22rgba%2834%2C%20197%2C%2094%2C%200.12%29%22%2C%22belowLineFillColorFalling%22%3A%22rgba%28239%2C%2068%2C%2068%2C%200.12%29%22%2C%22belowLineFillColorGrowingBottom%22%3A%22rgba%2834%2C%20197%2C%2094%2C%200%29%22%2C%22belowLineFillColorFallingBottom%22%3A%22rgba%28239%2C%2068%2C%2068%2C%200%29%22%2C%22symbolActiveColor%22%3A%22rgba%28245%2C%20158%2C%2011%2C%200.12%29%22%2C%22tabs%22%3A%5B%7B%22title%22%3A%22Indices%22%2C%22symbols%22%3A%5B%7B%22s%22%3A%22FOREXCOM%3ASPXUSD%22%2C%22d%22%3A%22S%26P%20500%22%7D%2C%7B%22s%22%3A%22FOREXCOM%3ANSXUSD%22%2C%22d%22%3A%22NASDAQ%22%7D%2C%7B%22s%22%3A%22FOREXCOM%3ADJI%22%2C%22d%22%3A%22Dow%20Jones%22%7D%2C%7B%22s%22%3A%22TVC%3AVIX%22%2C%22d%22%3A%22VIX%22%7D%5D%2C%22originalTitle%22%3A%22Indices%22%7D%2C%7B%22title%22%3A%22Crypto%22%2C%22symbols%22%3A%5B%7B%22s%22%3A%22BITSTAMP%3ABTCUSD%22%2C%22d%22%3A%22Bitcoin%22%7D%2C%7B%22s%22%3A%22BITSTAMP%3AETHUSD%22%2C%22d%22%3A%22Ethereum%22%7D%2C%7B%22s%22%3A%22BINANCE%3ASOLUSDT%22%2C%22d%22%3A%22Solana%22%7D%5D%2C%22originalTitle%22%3A%22Crypto%22%7D%2C%7B%22title%22%3A%22Tech%22%2C%22symbols%22%3A%5B%7B%22s%22%3A%22NASDAQ%3AAAPL%22%2C%22d%22%3A%22Apple%22%7D%2C%7B%22s%22%3A%22NASDAQ%3ANVDA%22%2C%22d%22%3A%22NVIDIA%22%7D%2C%7B%22s%22%3A%22NASDAQ%3AMSFT%22%2C%22d%22%3A%22Microsoft%22%7D%2C%7B%22s%22%3A%22NASDAQ%3AGOOGL%22%2C%22d%22%3A%22Google%22%7D%5D%2C%22originalTitle%22%3A%22Tech%22%7D%2C%7B%22title%22%3A%22Commodities%22%2C%22symbols%22%3A%5B%7B%22s%22%3A%22OANDA%3AXAUUSD%22%2C%22d%22%3A%22Gold%22%7D%2C%7B%22s%22%3A%22OANDA%3AXAGUSD%22%2C%22d%22%3A%22Silver%22%7D%2C%7B%22s%22%3A%22TVC%3AUSOIL%22%2C%22d%22%3A%22Crude%20Oil%22%7D%5D%2C%22originalTitle%22%3A%22Commodities%22%7D%5D%7D"
                    style={{ width: '100%', height: '400px' }}
                    title="Market Overview"
                  />
                </div>
                
                {/* AI Quick Insights Cards - From Daily Insights API */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Market Sentiment Card */}
                  <div className="bg-black/40 border border-zinc-800 p-3">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-[10px] text-amber-500 font-bold uppercase">AI MARKET OVERVIEW</span>
                        {dailyInsights?.insights?.market_overview ? (
                          <>
                            <p className="text-xs text-zinc-300 mt-1 leading-relaxed">
                              {dailyInsights.insights.market_overview.insight?.substring(0, 150)}...
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                dailyInsights.insights.market_overview.sentiment_label === 'BULLISH' ? 'bg-emerald-500/20 text-emerald-400' :
                                dailyInsights.insights.market_overview.sentiment_label === 'BEARISH' ? 'bg-red-500/20 text-red-400' :
                                'bg-amber-500/20 text-amber-400'
                              }`}>
                                {dailyInsights.insights.market_overview.sentiment_label || 'NEUTRAL'} ({dailyInsights.insights.market_overview.sentiment_pct || 0}%)
                              </span>
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-zinc-500 mt-1">Loading insights...</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Sector Insight Card */}
                  <div className="bg-black/40 border border-zinc-800 p-3">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-[10px] text-purple-500 font-bold uppercase">AI SECTOR INSIGHT</span>
                        {dailyInsights?.insights?.sector_insight ? (
                          <>
                            <p className="text-xs text-zinc-300 mt-1 leading-relaxed">
                              {dailyInsights.insights.sector_insight.insight?.substring(0, 150)}...
                            </p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className="text-[10px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                                {dailyInsights.insights.sector_insight.tech_strength || 'Tech: N/A'}
                              </span>
                              <span className="text-[10px] bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">
                                {dailyInsights.insights.sector_insight.crypto_strength || 'Crypto: N/A'}
                              </span>
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-zinc-500 mt-1">Loading insights...</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Trading Suggestion Card */}
                  <div className="bg-black/40 border border-zinc-800 p-3">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-[10px] text-emerald-500 font-bold uppercase">AI TRADING SUGGESTION</span>
                        {dailyInsights?.insights?.trading_suggestion ? (
                          <>
                            <p className="text-xs text-zinc-300 mt-1 leading-relaxed">
                              {dailyInsights.insights.trading_suggestion.insight?.substring(0, 150)}...
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                dailyInsights.insights.trading_suggestion.risk_level === 'LOW' ? 'bg-emerald-500/20 text-emerald-400' :
                                dailyInsights.insights.trading_suggestion.risk_level === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                                'bg-amber-500/20 text-amber-400'
                              }`}>
                                Risk: {dailyInsights.insights.trading_suggestion.risk_level || 'MODERATE'}
                              </span>
                              <span className="text-[10px] bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">
                                Volatility: {dailyInsights.insights.trading_suggestion.volatility || 'N/A'}
                              </span>
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-zinc-500 mt-1">Loading insights...</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* TOP MOVERS - Rich Gainers & Losers with Live Data */}
                <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="top-movers-section">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-black tracking-wider text-white uppercase" style={{ fontFamily: 'Barlow Condensed' }}>Top Market Movers</span>
                      <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded animate-pulse">LIVE</span>
                    </div>
                    {topMovers?.generated_at && (
                      <AccuracyBadge generatedAt={topMovers.generated_at} accuracy={96} />
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Top Gainers */}
                    <div className="bg-black/30 border border-emerald-500/20 p-3 rounded" data-testid="top-gainers">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs font-bold text-emerald-500 uppercase">Top Gainers</span>
                        <span className="text-[9px] bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded ml-auto">{topMovers?.gainers?.length || 0} stocks</span>
                      </div>
                      <div className="space-y-1">
                        {(topMovers?.gainers || dailyInsights?.insights?.top_gainers || []).slice(0, 7).map((stock, idx) => (
                          <div key={idx} className="flex items-center gap-2 py-1.5 border-b border-zinc-800/30 last:border-0 cursor-pointer hover:bg-emerald-500/5 transition-colors rounded px-1" data-testid={`gainer-${idx}`}
                            onClick={() => setSelectedMover(selectedMover?.symbol === stock.symbol ? null : { ...stock, direction: 'up' })}>
                            <span className="text-[10px] text-zinc-600 w-3">{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold text-white">{stock.symbol}</span>
                                {stock.name && <span className="text-[9px] text-zinc-500 truncate">{stock.name}</span>}
                              </div>
                              {stock.price && (
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[10px] text-zinc-400">${typeof stock.price === 'number' ? stock.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : stock.price}</span>
                                  {stock.volume_fmt && <span className="text-[9px] text-zinc-600">Vol: {stock.volume_fmt}</span>}
                                  {stock.type === 'crypto' && <span className="text-[8px] bg-amber-500/10 text-amber-400 px-1 rounded">CRYPTO</span>}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-xs font-black text-emerald-400">
                                {stock.change_pct !== undefined ? `+${stock.change_pct.toFixed(2)}%` : stock.change}
                              </span>
                              {stock.momentum && (
                                <div className="w-12 h-1 bg-zinc-800 rounded-full mt-1 ml-auto">
                                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, stock.momentum)}%` }} />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {(!topMovers?.gainers && !dailyInsights?.insights?.top_gainers) && (
                          <div className="py-3 text-center text-zinc-500 text-xs animate-pulse">Loading live data...</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Top Losers */}
                    <div className="bg-black/30 border border-red-500/20 p-3 rounded" data-testid="top-losers">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="w-4 h-4 text-red-500" />
                        <span className="text-xs font-bold text-red-500 uppercase">Top Losers</span>
                        <span className="text-[9px] bg-red-500/10 text-red-400 px-1.5 py-0.5 rounded ml-auto">{topMovers?.losers?.length || 0} stocks</span>
                      </div>
                      <div className="space-y-1">
                        {(topMovers?.losers || dailyInsights?.insights?.top_losers || []).slice(0, 7).map((stock, idx) => (
                          <div key={idx} className="flex items-center gap-2 py-1.5 border-b border-zinc-800/30 last:border-0 cursor-pointer hover:bg-red-500/5 transition-colors rounded px-1" data-testid={`loser-${idx}`}
                            onClick={() => setSelectedMover(selectedMover?.symbol === stock.symbol ? null : { ...stock, direction: 'down' })}>
                            <span className="text-[10px] text-zinc-600 w-3">{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold text-white">{stock.symbol}</span>
                                {stock.name && <span className="text-[9px] text-zinc-500 truncate">{stock.name}</span>}
                              </div>
                              {stock.price && (
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[10px] text-zinc-400">${typeof stock.price === 'number' ? stock.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : stock.price}</span>
                                  {stock.volume_fmt && <span className="text-[9px] text-zinc-600">Vol: {stock.volume_fmt}</span>}
                                  {stock.type === 'crypto' && <span className="text-[8px] bg-amber-500/10 text-amber-400 px-1 rounded">CRYPTO</span>}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-xs font-black text-red-400">
                                {stock.change_pct !== undefined ? `${stock.change_pct.toFixed(2)}%` : stock.change}
                              </span>
                              {stock.momentum && (
                                <div className="w-12 h-1 bg-zinc-800 rounded-full mt-1 ml-auto">
                                  <div className="h-full bg-red-500 rounded-full" style={{ width: `${Math.min(100, stock.momentum)}%` }} />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {(!topMovers?.losers && !dailyInsights?.insights?.top_losers) && (
                          <div className="py-3 text-center text-zinc-500 text-xs animate-pulse">Loading live data...</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Selected Mover — Chart + AI Signal Popup */}
                  {selectedMover && (
                    <div className="mt-3 bg-[#0a0a0a] border border-zinc-700 rounded-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300" data-testid="mover-chart-popup">
                      <div className="flex items-center justify-between p-3 border-b border-zinc-800">
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-black px-2 py-1 rounded ${selectedMover.direction === 'up' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                            {selectedMover.direction === 'up' ? 'GAINER' : 'LOSER'}
                          </span>
                          <span className="text-lg font-black text-white">{selectedMover.symbol}</span>
                          <span className="text-sm text-zinc-400">{selectedMover.name}</span>
                          <span className="text-sm font-bold text-white">${typeof selectedMover.price === 'number' ? selectedMover.price.toLocaleString(undefined, {minimumFractionDigits: 2}) : selectedMover.price}</span>
                          <span className={`text-sm font-bold ${selectedMover.change_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {selectedMover.change_pct >= 0 ? '+' : ''}{selectedMover.change_pct?.toFixed(2)}%
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-black px-3 py-1 rounded-full ${
                            selectedMover.change_pct > 3 ? 'bg-emerald-500/30 text-emerald-300' :
                            selectedMover.change_pct > 0 ? 'bg-emerald-500/20 text-emerald-400' :
                            selectedMover.change_pct > -3 ? 'bg-red-500/20 text-red-400' :
                            'bg-red-500/30 text-red-300'
                          }`}>
                            {selectedMover.change_pct > 3 ? 'STRONG BUY' :
                             selectedMover.change_pct > 0 ? 'BUY' :
                             selectedMover.change_pct > -3 ? 'HOLD' : 'SELL'}
                          </span>
                          <button onClick={() => setSelectedMover(null)} className="text-zinc-500 hover:text-white text-sm px-2">Close</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3">
                        {/* TradingView 5-day chart */}
                        <div className="lg:col-span-2" style={{ height: '280px' }}>
                          <iframe
                            key={selectedMover.symbol}
                            src={`https://s.tradingview.com/widgetembed/?frameElementId=tv_mover&symbol=${
                              selectedMover.type === 'crypto' ? `COINBASE:${selectedMover.symbol}USD` : selectedMover.symbol
                            }&interval=60&theme=dark&style=1&timezone=America%2FNew_York&studies=RSI%40tv-basicstudies&withdateranges=1&hide_side_toolbar=0&allow_symbol_change=0&save_image=0&hideideas=1&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%5D&disabled_features=%5B%5D&locale=en&utm_source=&utm_medium=widget&utm_campaign=chart`}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title={`${selectedMover.symbol} chart`}
                          />
                        </div>
                        {/* Quick Stats Panel */}
                        <div className="p-4 border-l border-zinc-800 flex flex-col gap-3">
                          <div className="text-xs font-bold text-zinc-400 uppercase">Quick Stats</div>
                          <div className="space-y-2.5">
                            <div className="flex justify-between">
                              <span className="text-[11px] text-zinc-500">Price</span>
                              <span className="text-[11px] font-bold text-white">${typeof selectedMover.price === 'number' ? selectedMover.price.toLocaleString(undefined, {minimumFractionDigits: 2}) : selectedMover.price}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[11px] text-zinc-500">Day Change</span>
                              <span className={`text-[11px] font-bold ${selectedMover.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {selectedMover.change >= 0 ? '+' : ''}${selectedMover.change?.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[11px] text-zinc-500">Prev Close</span>
                              <span className="text-[11px] text-zinc-300">${selectedMover.prev_close?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[11px] text-zinc-500">Volume</span>
                              <span className="text-[11px] text-zinc-300">{selectedMover.volume_fmt}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[11px] text-zinc-500">Day Range</span>
                              <span className="text-[11px] text-zinc-300">${selectedMover.day_low} - ${selectedMover.day_high}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[11px] text-zinc-500">Momentum</span>
                              <div className="flex items-center gap-1.5">
                                <div className="w-16 h-1.5 bg-zinc-800 rounded-full">
                                  <div className={`h-full rounded-full ${selectedMover.momentum >= 50 ? 'bg-emerald-500' : selectedMover.momentum >= 25 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${selectedMover.momentum}%` }} />
                                </div>
                                <span className="text-[11px] font-bold text-zinc-300">{selectedMover.momentum}%</span>
                              </div>
                            </div>
                            {selectedMover.category && (
                              <div className="flex justify-between">
                                <span className="text-[11px] text-zinc-500">Sector</span>
                                <span className="text-[11px] text-amber-400 capitalize">{selectedMover.category}</span>
                              </div>
                            )}
                          </div>
                          <div className={`mt-auto p-2 rounded text-center text-xs font-bold ${
                            selectedMover.change_pct > 3 ? 'bg-emerald-500/20 text-emerald-400' :
                            selectedMover.change_pct > 0 ? 'bg-emerald-500/10 text-emerald-400' :
                            selectedMover.change_pct > -3 ? 'bg-amber-500/10 text-amber-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {selectedMover.change_pct > 3 ? 'Strong upward momentum — watch for continuation' :
                             selectedMover.change_pct > 0 ? 'Positive trend — look for support levels' :
                             selectedMover.change_pct > -3 ? 'Consolidating — wait for clear direction' :
                             'Selling pressure — watch for reversal signals'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* AI POWER PREDICTIONS - Enhanced Insights */}
                  <div className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-amber-500/10 border border-purple-500/30 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      <span className="text-sm font-bold text-purple-400 uppercase">LIONAI Power Predictions</span>
                      <span className="text-[10px] bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full ml-2">AI-POWERED</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* 24H Market Prediction */}
                      <div className="bg-black/40 border border-zinc-700 p-3 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-3 h-3 text-blue-400" />
                          <span className="text-[10px] text-blue-400 font-bold uppercase">24H Outlook</span>
                        </div>
                        <p className="text-xs text-zinc-300 leading-relaxed">
                          {dailyInsights?.insights?.predictions?.short_term || 
                           "Expect continued volatility with potential mean-reversion in oversold tech names. Watch VIX for sentiment shifts. Key level: S&P 6,600 support."}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${
                            (dailyInsights?.insights?.predictions?.short_term_bias || 'NEUTRAL') === 'BULLISH' ? 'bg-emerald-500/20 text-emerald-400' :
                            (dailyInsights?.insights?.predictions?.short_term_bias || 'NEUTRAL') === 'BEARISH' ? 'bg-red-500/20 text-red-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {dailyInsights?.insights?.predictions?.short_term_bias || 'CAUTIOUS'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Weekly Trend Prediction */}
                      <div className="bg-black/40 border border-zinc-700 p-3 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-3 h-3 text-amber-400" />
                          <span className="text-[10px] text-amber-400 font-bold uppercase">Weekly Trend</span>
                        </div>
                        <p className="text-xs text-zinc-300 leading-relaxed">
                          {dailyInsights?.insights?.predictions?.weekly_trend || 
                           "Consolidation phase likely. Watch for earnings catalysts mid-week. Crypto showing relative strength vs equities. Gold remains defensive hedge."}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${
                            (dailyInsights?.insights?.predictions?.weekly_bias || 'NEUTRAL') === 'BULLISH' ? 'bg-emerald-500/20 text-emerald-400' :
                            (dailyInsights?.insights?.predictions?.weekly_bias || 'NEUTRAL') === 'BEARISH' ? 'bg-red-500/20 text-red-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {dailyInsights?.insights?.predictions?.weekly_bias || 'CONSOLIDATION'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Hot Opportunities */}
                      <div className="bg-black/40 border border-zinc-700 p-3 rounded">
                        <div className="flex items-center gap-2 mb-2">
                          <Flame className="w-3 h-3 text-orange-400" />
                          <span className="text-[10px] text-orange-400 font-bold uppercase">Hot Opportunities</span>
                        </div>
                        <p className="text-xs text-zinc-300 leading-relaxed">
                          {dailyInsights?.insights?.predictions?.opportunities || 
                           "Watch oversold bounce plays in NVDA, META. Crypto momentum favors SOL, ETH. Gold breaking out - consider GLD exposure."}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(dailyInsights?.insights?.predictions?.hot_picks || ['NVDA', 'SOL', 'GLD']).map((pick, idx) => (
                            <span key={idx} className="text-[10px] px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded font-bold">
                              {pick}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* AI Confidence Score */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-700">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-zinc-500">AI Confidence:</span>
                          <div className="w-24 h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"
                              style={{ width: `${dailyInsights?.insights?.predictions?.confidence || 72}%` }}
                            />
                          </div>
                          <span className="text-xs font-bold text-purple-400">
                            {dailyInsights?.insights?.predictions?.confidence || 72}%
                          </span>
                        </div>
                        <span className="text-[10px] text-zinc-600">
                          Based on {dailyInsights?.insights?.predictions?.data_points || '847'} data points
                        </span>
                      </div>
                      <span className="text-[10px] text-zinc-600">
                        Powered by LIONAI • Updated: {new Date().toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <p className="text-[10px] text-center text-zinc-600 mt-3">
                  ⚠️ Not financial advice. For educational purposes only. Past performance does not guarantee future results.
                </p>
              </section>

              {/* SUPER AI FEATURES - Earnings, Sentiment, Economic Calendar */}
              <SuperAIFeatures
                earningsWhisper={earningsWhisper}
                socialSentiment={socialSentiment}
                economicCalendar={economicCalendar}
                fetchEarningsWhisper={fetchEarningsWhisper}
              />

              {/* 📊 CHART VIEW - TradingView Professional Charts - MEGA ENHANCED */}
              <section className="relative" data-testid="chart-view-section">
                {/* Pro-Only Advanced Features Overlay */}
                {!isPro && (
                  <div className="absolute top-0 right-0 z-20 m-4">
                    <div 
                      className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full cursor-pointer hover:bg-amber-500/30 transition-colors"
                      onClick={() => showUpgradePopup("Advanced Chart Indicators")}
                    >
                      <Lock className="w-3 h-3 text-amber-500" />
                      <span className="text-xs font-bold text-amber-500">PRO: Unlock All Indicators</span>
                    </div>
                  </div>
                )}
                
                <div className="bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 border-2 border-blue-500/30 p-4">
                  {/* Header with Enhanced Search */}
                  <div className="flex flex-col gap-4 mb-4">
                    {/* Title Row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center rounded-lg">
                          <LineChart className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                            📊 CHART VIEW
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">TradingView</span>
                            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full animate-pulse">LIVE</span>
                          </h2>
                          <p className="text-xs text-zinc-500">Search ANY asset worldwide - Stocks, Crypto, Forex, Commodities & more</p>
                        </div>
                      </div>
                      
                      {/* Timeframe Selector */}
                      <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-700 rounded-lg p-1">
                        {[
                          { label: '1m', value: '1' },
                          { label: '5m', value: '5' },
                          { label: '15m', value: '15' },
                          { label: '1H', value: '60' },
                          { label: '4H', value: '240' },
                          { label: '1D', value: 'D' },
                          { label: '1W', value: 'W' },
                        ].map(tf => (
                          <button
                            key={tf.value}
                            onClick={() => setChartInterval(tf.value)}
                            className={`px-2 py-1 text-xs rounded transition-colors ${chartInterval === tf.value ? 'bg-blue-500 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
                            data-testid={`chart-tf-${tf.value}`}
                          >
                            {tf.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* MEGA SEARCH BAR - Search Any TradingView Asset */}
                    <div className="relative">
                      <div className="flex items-center gap-3">
                        {/* Current Symbol Display */}
                        <div className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/50 rounded-lg">
                          <span className="text-lg font-bold text-blue-400" data-testid="current-chart-symbol">{chartSymbol}</span>
                        </div>
                        
                        {/* Search Input */}
                        <div className="flex-1 relative">
                          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                          <input
                            type="text"
                            placeholder="Search any symbol... AAPL, BTCUSD, EURUSD, GOLD, SPX, and more..."
                            value={chartSearchQuery}
                            onChange={(e) => setChartSearchQuery(e.target.value)}
                            onFocus={() => setShowChartSearch(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && chartSearchQuery) {
                                selectChartSymbol(chartSearchQuery.toUpperCase());
                              }
                            }}
                            className="w-full pl-12 pr-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:border-blue-500 focus:outline-none transition-colors"
                            data-testid="chart-search-input"
                          />
                          {chartSearchLoading && (
                            <RefreshCw className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 animate-spin" />
                          )}
                        </div>
                      </div>
                      
                      {/* Search Results Dropdown */}
                      {showChartSearch && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-40 max-h-96 overflow-hidden">
                          {/* Search Results */}
                          {chartSearchQuery && chartSearchResults.length > 0 && (
                            <div className="max-h-64 overflow-y-auto">
                              <div className="px-3 py-2 bg-zinc-800/50 border-b border-zinc-700">
                                <p className="text-xs text-zinc-500 uppercase tracking-wider">Search Results ({chartSearchResults.length})</p>
                              </div>
                              {chartSearchResults.map((item, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => selectChartSymbol(item.symbol)}
                                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-500/10 transition-colors border-b border-zinc-800 last:border-b-0"
                                  data-testid={`chart-result-${item.symbol}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 flex items-center justify-center rounded ${
                                      item.type === 'stock' ? 'bg-amber-500/20 text-amber-500' :
                                      item.type === 'crypto' ? 'bg-purple-500/20 text-purple-500' :
                                      item.type === 'forex' ? 'bg-emerald-500/20 text-emerald-500' :
                                      item.type === 'commodity' ? 'bg-yellow-500/20 text-yellow-500' :
                                      'bg-blue-500/20 text-blue-500'
                                    }`}>
                                      {item.type === 'stock' ? <BarChart3 className="w-4 h-4" /> :
                                       item.type === 'crypto' ? <Bitcoin className="w-4 h-4" /> :
                                       item.type === 'forex' ? <Globe className="w-4 h-4" /> :
                                       item.type === 'commodity' ? <Gem className="w-4 h-4" /> :
                                       <LineChart className="w-4 h-4" />}
                                    </div>
                                    <div className="text-left">
                                      <p className="font-bold text-white">{item.symbol}</p>
                                      <p className="text-xs text-zinc-500">{item.name}</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      item.type === 'stock' ? 'bg-amber-500/20 text-amber-400' :
                                      item.type === 'crypto' ? 'bg-purple-500/20 text-purple-400' :
                                      item.type === 'forex' ? 'bg-emerald-500/20 text-emerald-400' :
                                      item.type === 'commodity' ? 'bg-yellow-500/20 text-yellow-400' :
                                      'bg-blue-500/20 text-blue-400'
                                    }`}>
                                      {item.exchange}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* No Results */}
                          {chartSearchQuery && !chartSearchLoading && chartSearchResults.length === 0 && (
                            <div className="p-4 text-center">
                              <p className="text-zinc-400">No results for "{chartSearchQuery}"</p>
                              <p className="text-xs text-zinc-600 mt-1">Try entering the exact symbol or press Enter to search</p>
                              <button
                                onClick={() => selectChartSymbol(chartSearchQuery.toUpperCase())}
                                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-bold hover:bg-blue-600 transition-colors"
                              >
                                Chart "{chartSearchQuery.toUpperCase()}" anyway
                              </button>
                            </div>
                          )}
                          
                          {/* Recent & Popular when no search query */}
                          {!chartSearchQuery && (
                            <div className="p-3">
                              {/* Recent Symbols */}
                              {recentChartSymbols.length > 0 && (
                                <div className="mb-4">
                                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> Recent
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {recentChartSymbols.map(sym => (
                                      <button
                                        key={sym}
                                        onClick={() => selectChartSymbol(sym)}
                                        className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${chartSymbol === sym ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
                                      >
                                        {sym}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Quick Categories */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {/* Stocks */}
                                <div>
                                  <p className="text-[10px] text-amber-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                                    <BarChart3 className="w-3 h-3" /> Stocks
                                  </p>
                                  <div className="space-y-1">
                                    {['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'TSLA'].map(sym => (
                                      <button
                                        key={sym}
                                        onClick={() => selectChartSymbol(sym)}
                                        className={`w-full px-2 py-1 text-xs rounded text-left transition-colors ${chartSymbol === sym ? 'bg-blue-500 text-white' : 'bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700'}`}
                                      >
                                        {sym}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                
                                {/* Crypto */}
                                <div>
                                  <p className="text-[10px] text-purple-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                                    <Bitcoin className="w-3 h-3" /> Crypto
                                  </p>
                                  <div className="space-y-1">
                                    {['BTCUSD', 'ETHUSD', 'SOLUSD', 'XRPUSD', 'DOGEUSD'].map(sym => (
                                      <button
                                        key={sym}
                                        onClick={() => selectChartSymbol(sym)}
                                        className={`w-full px-2 py-1 text-xs rounded text-left transition-colors ${chartSymbol === sym ? 'bg-blue-500 text-white' : 'bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700'}`}
                                      >
                                        {sym}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                
                                {/* Forex */}
                                <div>
                                  <p className="text-[10px] text-emerald-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                                    <Globe className="w-3 h-3" /> Forex
                                  </p>
                                  <div className="space-y-1">
                                    {['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD'].map(sym => (
                                      <button
                                        key={sym}
                                        onClick={() => selectChartSymbol(sym)}
                                        className={`w-full px-2 py-1 text-xs rounded text-left transition-colors ${chartSymbol === sym ? 'bg-blue-500 text-white' : 'bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700'}`}
                                      >
                                        {sym}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                
                                {/* Commodities & Indices */}
                                <div>
                                  <p className="text-[10px] text-yellow-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                                    <Gem className="w-3 h-3" /> More
                                  </p>
                                  <div className="space-y-1">
                                    {['XAUUSD', 'USOIL', 'SPX', 'DJI', 'VIX'].map(sym => (
                                      <button
                                        key={sym}
                                        onClick={() => selectChartSymbol(sym)}
                                        className={`w-full px-2 py-1 text-xs rounded text-left transition-colors ${chartSymbol === sym ? 'bg-blue-500 text-white' : 'bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700'}`}
                                      >
                                        {sym}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Custom Symbol Tip */}
                              <div className="mt-3 pt-3 border-t border-zinc-800">
                                <p className="text-xs text-zinc-500 text-center">
                                  💡 Type any TradingView symbol (e.g., <span className="text-blue-400">BINANCE:BTCUSDT</span>, <span className="text-blue-400">FX:EURUSD</span>) and press Enter
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* TradingView Chart Widget - Enhanced for Pro Users */}
                  <div className="relative bg-black rounded-lg overflow-hidden" style={{ height: isPro ? '550px' : '500px' }}>
                    {/* Pro Users: Full TradingView Experience */}
                    {isPro ? (
                      <iframe
                        key={`pro-${chartSymbol}-${chartInterval}`}
                        src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=${chartSymbol}&interval=${chartInterval}&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=1a1a1a&studies=RSI%40tv-basicstudies&theme=dark&style=1&timezone=exchange&withdateranges=1&showpopupbutton=1&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%22use_localstorage_for_settings%22%2C%22save_chart_properties_to_local_storage%22%5D&disabled_features=%5B%5D&locale=en&utm_source=ecolionsmarkets.online&utm_medium=widget&utm_campaign=chart&allow_symbol_change=1&details=1&hotlist=1&calendar=1`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title={`TradingView Pro Chart - ${chartSymbol}`}
                        allowFullScreen
                      />
                    ) : (
                      <iframe
                        key={`free-${chartSymbol}-${chartInterval}`}
                        src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=${chartSymbol}&interval=${chartInterval}&hidesidetoolbar=1&symboledit=0&saveimage=0&toolbarbg=f1f3f6&studies=%5B%5D&theme=dark&style=1&timezone=exchange&withdateranges=0&showpopupbutton=0&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%5D&disabled_features=%5B%22header_widget%22%2C%22left_toolbar%22%2C%22context_menus%22%5D&locale=en&utm_source=ecolionsmarkets.online&utm_medium=widget&utm_campaign=chart`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title={`TradingView Chart - ${chartSymbol}`}
                        allowFullScreen
                      />
                    )}
                    
                    {/* Small Fullscreen Button - Bottom Right Corner (Pro Only) */}
                    {isPro && (
                      <div className="absolute bottom-2 right-2 z-10">
                        <button
                          onClick={() => setChartFullscreen(true)}
                          className="flex items-center justify-center w-7 h-7 bg-black/60 border border-zinc-600 rounded hover:bg-black/80 hover:border-blue-500 transition-colors cursor-pointer"
                          data-testid="chart-fullscreen-btn"
                          title="Fullscreen"
                        >
                          <Maximize2 className="w-3.5 h-3.5 text-zinc-300 hover:text-white" />
                        </button>
                      </div>
                    )}
                    
                    {/* Pro Features Lock Overlay - Only for Free Users */}
                    {!isPro && (
                      <div className="absolute bottom-4 left-4 right-4">
                        <div 
                          className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-500/20 to-purple-500/20 border border-amber-500/30 rounded-lg backdrop-blur-sm cursor-pointer hover:bg-amber-500/30 transition-colors"
                          onClick={() => showUpgradePopup("Advanced Chart Tools")}
                        >
                          <div className="flex items-center gap-3">
                            <Lock className="w-5 h-5 text-amber-500" />
                            <div>
                              <p className="text-sm font-bold text-white">Unlock Pro Drawing Tools</p>
                              <p className="text-xs text-zinc-400">Trend lines, RSI, MACD, Fibonacci, annotations & more</p>
                            </div>
                          </div>
                          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                            <Crown className="w-3 h-3 mr-1" /> Upgrade
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Chart Info Footer */}
                  <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
                    <div className="flex items-center gap-4">
                      <span>Powered by TradingView</span>
                      <span>•</span>
                      <span>Real-time data</span>
                      {isPro && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/40 rounded text-emerald-400">
                            <Unlock className="w-3 h-3" />
                            <span className="text-[10px] font-bold">PRO DRAWING TOOLS</span>
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isPro ? (
                        <span className="flex items-center gap-1 text-emerald-500">
                          <Unlock className="w-3 h-3" /> Pro chart access
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-500">
                          <Lock className="w-3 h-3" /> Basic view • Upgrade for full access
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </section>
              
              {/* 🖥️ FULLSCREEN CHART MODAL - PRO ONLY */}
              {chartFullscreen && isPro && (
                <div className="fixed inset-0 z-50 bg-black" data-testid="chart-fullscreen-modal">
                  
                  {/* Fullscreen TradingView Chart - Full Screen */}
                  <iframe
                    key={`fullscreen-${chartSymbol}-${chartInterval}`}
                    src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_fullscreen&symbol=${chartSymbol}&interval=${chartInterval}&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=1a1a1a&studies=RSI%40tv-basicstudies%2CMACD%40tv-basicstudies&theme=dark&style=1&timezone=exchange&withdateranges=1&showpopupbutton=1&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%22use_localstorage_for_settings%22%2C%22save_chart_properties_to_local_storage%22%5D&disabled_features=%5B%5D&locale=en&utm_source=ecolionsmarkets.online&utm_medium=widget&utm_campaign=fullscreen&allow_symbol_change=1&details=1&hotlist=1&calendar=1`}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title={`TradingView Fullscreen - ${chartSymbol}`}
                    allowFullScreen
                  />
                  
                  {/* DRAGGABLE Control Bar - Can be moved anywhere */}
                  <div 
                    className="absolute z-10 flex items-center gap-3"
                    style={{
                      left: controlsPosition.x !== null ? `${controlsPosition.x}px` : 'auto',
                      top: controlsPosition.y !== null ? `${controlsPosition.y}px` : 'auto',
                      right: controlsPosition.x === null ? '16px' : 'auto',
                      bottom: controlsPosition.y === null ? '16px' : 'auto',
                      cursor: isDragging === 'controls' ? 'grabbing' : 'grab',
                    }}
                    onMouseDown={(e) => handleDragStart(e, 'controls')}
                    data-testid="draggable-controls"
                  >
                    {/* Drag Handle */}
                    <div className="flex items-center gap-1 px-2 py-2 bg-zinc-800/90 border border-zinc-600 rounded-lg backdrop-blur-sm cursor-grab" title="Drag to move">
                      <Menu className="w-4 h-4 text-zinc-400" />
                    </div>
                    
                    {/* Symbol Display */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-black/90 border border-zinc-700 rounded-lg backdrop-blur-sm">
                      <span className="text-lg font-bold text-blue-400">{chartSymbol}</span>
                      <span className="text-xs bg-purple-500/30 text-purple-400 px-2 py-0.5 rounded animate-pulse">LIVE</span>
                    </div>
                    
                    {/* Timeframe Selector */}
                    <div className="flex items-center gap-1 bg-black/90 border border-zinc-700 rounded-lg p-1 backdrop-blur-sm">
                      {[
                        { label: '1m', value: '1' },
                        { label: '5m', value: '5' },
                        { label: '15m', value: '15' },
                        { label: '1H', value: '60' },
                        { label: '4H', value: '240' },
                        { label: '1D', value: 'D' },
                        { label: '1W', value: 'W' },
                      ].map(tf => (
                        <button
                          key={tf.value}
                          onClick={(e) => { e.stopPropagation(); setChartInterval(tf.value); }}
                          onMouseDown={(e) => e.stopPropagation()}
                          className={`px-2 py-1 text-xs rounded transition-colors ${chartInterval === tf.value ? 'bg-blue-500 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
                        >
                          {tf.label}
                        </button>
                      ))}
                    </div>
                    
                    {/* Reset Position Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); resetControlPositions(); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 px-2 py-2 bg-zinc-800/90 border border-zinc-600 rounded-lg hover:bg-zinc-700 transition-colors"
                      title="Reset positions"
                    >
                      <RefreshCw className="w-3 h-3 text-zinc-400" />
                    </button>
                    
                    {/* Exit Fullscreen Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setChartFullscreen(false); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 px-3 py-2 bg-red-500/90 border border-red-400 rounded-lg shadow-lg hover:bg-red-500 transition-colors"
                      data-testid="chart-fullscreen-close"
                    >
                      <Minimize2 className="w-4 h-4 text-white" />
                      <span className="text-xs font-bold text-white">EXIT</span>
                    </button>
                  </div>
                  
                  {/* DRAGGABLE Quick Search - Can be moved anywhere */}
                  <div 
                    className="absolute z-10"
                    style={{
                      left: searchPosition.x !== null ? `${searchPosition.x}px` : '16px',
                      top: searchPosition.y !== null ? `${searchPosition.y}px` : 'auto',
                      bottom: searchPosition.y === null ? '16px' : 'auto',
                      cursor: isDragging === 'search' ? 'grabbing' : 'grab',
                    }}
                    onMouseDown={(e) => handleDragStart(e, 'search')}
                    data-testid="draggable-search"
                  >
                    <div className="flex items-center gap-2 bg-black/90 border border-zinc-700 rounded-lg backdrop-blur-sm p-1">
                      {/* Drag Handle */}
                      <div className="px-2 py-1 cursor-grab" title="Drag to move">
                        <Menu className="w-4 h-4 text-zinc-400" />
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <input
                          type="text"
                          placeholder="Search symbol..."
                          className="w-40 pl-10 pr-4 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-white text-sm placeholder:text-zinc-500 focus:border-blue-500 focus:outline-none"
                          onMouseDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value) {
                              selectChartSymbol(e.target.value.toUpperCase());
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* ESC hint - Top Right (minimal) */}
                  <div className="absolute top-3 right-3 z-10">
                    <span className="text-xs text-zinc-600 bg-black/50 px-2 py-1 rounded">Drag controls anywhere • Press ESC to exit</span>
                  </div>
                </div>
              )}

              {/* 🧠 AI DAILY INSIGHTS - PRO ONLY (3 New Sections) */}
              <section className="relative">
                {/* Blurry Preview for Non-Pro Users */}
                {!isPro && (
                  <div 
                    className="absolute inset-0 backdrop-blur-md bg-black/50 z-10 flex flex-col items-center justify-center cursor-pointer hover:bg-black/40 transition-colors"
                    onClick={() => showUpgradePopup("AI Daily Insights")}
                    data-testid="insights-paywall"
                  >
                    <Lock className="w-12 h-12 text-amber-500 mb-3" />
                    <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                      PRO ECOPRENEUR EXCLUSIVE
                    </h3>
                    <p className="text-sm text-zinc-400 mb-4 text-center px-4">
                      Unlock AI-powered daily market insights generated by LIONAI
                    </p>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                      <Crown className="w-4 h-4 mr-2" /> Upgrade to Pro
                    </Button>
                  </div>
                )}
                
                <div className={`bg-gradient-to-br from-purple-500/10 via-amber-500/5 to-emerald-500/10 border-2 border-purple-500/30 p-6 ${!isPro ? 'pointer-events-none' : ''}`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-500/20 flex items-center justify-center rounded-lg">
                        <Sparkles className="w-6 h-6 text-purple-500" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold flex items-center gap-2 text-purple-400" style={{ fontFamily: 'Barlow Condensed' }}>
                          🧠 AI DAILY INSIGHTS
                          <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded ml-2">POWERED BY LIONAI</span>
                        </h2>
                        <p className="text-xs text-zinc-500">AI-generated insights • Auto-refresh every 24 hours • Data available until next refresh</p>
                      </div>
                    </div>
                    {isPro && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">
                          {dailyInsights?.generated_at ? `Updated: ${new Date(dailyInsights.generated_at).toLocaleString()}` : 'Loading...'}
                        </span>
                        <Button 
                          onClick={fetchDailyInsights}
                          disabled={loadingInsights}
                          variant="outline"
                          size="sm"
                          className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                          data-testid="refresh-insights-btn"
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${loadingInsights ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {loadingInsights ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="bg-black/40 border border-zinc-800 p-5 animate-pulse">
                          <div className="h-4 bg-zinc-800 rounded w-32 mb-3"></div>
                          <div className="h-3 bg-zinc-800 rounded w-full mb-2"></div>
                          <div className="h-3 bg-zinc-800 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-zinc-800 rounded w-1/2"></div>
                        </div>
                      ))}
                    </div>
                  ) : dailyInsights?.insights ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* AI MARKET OVERVIEW */}
                      <div className="bg-black/50 border border-amber-500/30 p-5 rounded-lg hover:border-amber-500/50 transition-colors" data-testid="insight-market-overview">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-amber-500/20 rounded flex items-center justify-center">
                            <BarChart3 className="w-4 h-4 text-amber-500" />
                          </div>
                          <h3 className="text-sm font-bold text-amber-500 uppercase tracking-wider">
                            {dailyInsights.insights.market_overview?.title || "AI MARKET OVERVIEW"}
                          </h3>
                        </div>
                        <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                          {dailyInsights.insights.market_overview?.insight}
                        </p>
                        <div className="flex items-center justify-between text-xs">
                          <span className={`px-2 py-1 rounded font-bold ${
                            dailyInsights.insights.market_overview?.sentiment_label === 'BULLISH' ? 'bg-emerald-500/20 text-emerald-400' :
                            dailyInsights.insights.market_overview?.sentiment_label === 'BEARISH' ? 'bg-red-500/20 text-red-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {dailyInsights.insights.market_overview?.sentiment_label} ({dailyInsights.insights.market_overview?.sentiment_pct}%)
                          </span>
                        </div>
                        {dailyInsights.insights.market_overview?.key_levels && (
                          <p className="text-xs text-zinc-500 mt-2 italic">
                            📊 {dailyInsights.insights.market_overview.key_levels}
                          </p>
                        )}
                      </div>
                      
                      {/* AI SECTOR INSIGHT */}
                      <div className="bg-black/50 border border-emerald-500/30 p-5 rounded-lg hover:border-emerald-500/50 transition-colors" data-testid="insight-sector">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-emerald-500/20 rounded flex items-center justify-center">
                            <PieChart className="w-4 h-4 text-emerald-500" />
                          </div>
                          <h3 className="text-sm font-bold text-emerald-500 uppercase tracking-wider">
                            {dailyInsights.insights.sector_insight?.title || "AI SECTOR INSIGHT"}
                          </h3>
                        </div>
                        <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                          {dailyInsights.insights.sector_insight?.insight}
                        </p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded">
                            Tech: {dailyInsights.insights.sector_insight?.tech_strength}
                          </span>
                          <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                            Crypto: {dailyInsights.insights.sector_insight?.crypto_strength}
                          </span>
                        </div>
                        {dailyInsights.insights.sector_insight?.hot_sector && (
                          <p className="text-xs text-emerald-400 mt-2 font-bold">
                            🔥 Hot Sector: {dailyInsights.insights.sector_insight.hot_sector}
                          </p>
                        )}
                      </div>
                      
                      {/* AI TRADING SUGGESTION */}
                      <div className="bg-black/50 border border-purple-500/30 p-5 rounded-lg hover:border-purple-500/50 transition-colors" data-testid="insight-trading">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-purple-500/20 rounded flex items-center justify-center">
                            <Target className="w-4 h-4 text-purple-500" />
                          </div>
                          <h3 className="text-sm font-bold text-purple-500 uppercase tracking-wider">
                            {dailyInsights.insights.trading_suggestion?.title || "AI TRADING SUGGESTION"}
                          </h3>
                        </div>
                        <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                          {dailyInsights.insights.trading_suggestion?.insight}
                        </p>
                        <div className="flex flex-wrap gap-2 text-xs mb-2">
                          <span className={`px-2 py-1 rounded font-bold ${
                            dailyInsights.insights.trading_suggestion?.risk_level === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                            dailyInsights.insights.trading_suggestion?.risk_level === 'MODERATE' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-emerald-500/20 text-emerald-400'
                          }`}>
                            Risk: {dailyInsights.insights.trading_suggestion?.risk_level}
                          </span>
                          <span className="px-2 py-1 bg-zinc-800 text-zinc-400 rounded">
                            Volatility: {dailyInsights.insights.trading_suggestion?.avg_movement}
                          </span>
                        </div>
                        {dailyInsights.insights.trading_suggestion?.strategy && (
                          <p className="text-xs text-purple-400 mt-2 font-bold">
                            💡 Strategy: {dailyInsights.insights.trading_suggestion.strategy}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Preview Data for Blur Effect (Non-Pro) */
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-black/50 border border-amber-500/30 p-5 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-amber-500/20 rounded flex items-center justify-center">
                            <BarChart3 className="w-4 h-4 text-amber-500" />
                          </div>
                          <h3 className="text-sm font-bold text-amber-500 uppercase">AI MARKET OVERVIEW</h3>
                        </div>
                        <p className="text-sm text-zinc-400">Markets showing strong momentum with bullish signals across major indices...</p>
                        <span className="inline-block mt-2 px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded font-bold">BULLISH (68%)</span>
                      </div>
                      <div className="bg-black/50 border border-emerald-500/30 p-5 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-emerald-500/20 rounded flex items-center justify-center">
                            <PieChart className="w-4 h-4 text-emerald-500" />
                          </div>
                          <h3 className="text-sm font-bold text-emerald-500 uppercase">AI SECTOR INSIGHT</h3>
                        </div>
                        <p className="text-sm text-zinc-400">Tech sector leading gains with NVDA and AAPL showing strength...</p>
                        <span className="inline-block mt-2 px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded font-bold">🔥 Technology</span>
                      </div>
                      <div className="bg-black/50 border border-purple-500/30 p-5 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-purple-500/20 rounded flex items-center justify-center">
                            <Target className="w-4 h-4 text-purple-500" />
                          </div>
                          <h3 className="text-sm font-bold text-purple-500 uppercase">AI TRADING SUGGESTION</h3>
                        </div>
                        <p className="text-sm text-zinc-400">Moderate volatility suggests swing trading opportunities on pullbacks...</p>
                        <span className="inline-block mt-2 px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded font-bold">MODERATE Risk</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Top Movers Summary */}
                  {isPro && dailyInsights?.insights && (
                    <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-center gap-8 text-sm">
                      <div className="flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                        <span className="text-zinc-400">Top Gainer:</span>
                        <span className="text-emerald-500 font-bold">{dailyInsights.insights.top_gainer?.symbol}</span>
                        <span className="text-emerald-400 text-xs">{dailyInsights.insights.top_gainer?.change}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ArrowDownRight className="w-4 h-4 text-red-500" />
                        <span className="text-zinc-400">Top Loser:</span>
                        <span className="text-red-500 font-bold">{dailyInsights.insights.top_loser?.symbol}</span>
                        <span className="text-red-400 text-xs">{dailyInsights.insights.top_loser?.change}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-4 flex items-center justify-center gap-4">
                    <AccuracyBadge generatedAt={dailyInsights?.generated_at} accuracy={89} />
                    <span className="text-[9px] text-zinc-600">
                      {dailyInsights?.insights?.ai_generated ? 'LIONAI Gemini 3 Flash' : 'Rule-based'} • Not financial advice
                    </span>
                  </div>
                </div>
              </section>

              {/* AI CRYPTO PREDICTIONS - PRO ONLY */}
              <CryptoPredictions
                cryptoPredictions={cryptoPredictions}
                loadingCryptoPredictions={loadingCryptoPredictions}
                fetchCryptoPredictions={fetchCryptoPredictions}
                isPro={isPro}
                showUpgradePopup={showUpgradePopup}
              />

              {/* AI DAILY OPPORTUNITIES - Pro Only */}
              {isPro && (
                <DailyOpportunities
                  dailyOpportunities={dailyOpportunities}
                  loadingOpportunities={loadingOpportunities}
                  refreshOpportunities={refreshOpportunities}
                  fetchDailyOpportunities={fetchDailyOpportunities}
                />
              )}

              {/* AI WHALE TRACKER - Institutional Money Flow */}
              {isPro && (
                <WhaleTracker
                  whaleTracker={whaleTracker}
                  fetchWhaleTracker={fetchWhaleTracker}
                />
              )}
            </div>
          )}

          {activeTab === "news" && (
            <div className="space-y-6">
              {!isPro ? (
                /* Locked News for Free Users */
                <div 
                  className="text-center py-16 bg-[#111111] border border-amber-500/30 cursor-pointer hover:bg-amber-500/5 transition-colors"
                  onClick={() => showUpgradePopup("Market News Feed")}
                >
                  <Lock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    MARKET NEWS FEED
                  </h2>
                  <p className="text-zinc-400 mb-2">Real-time news from Yahoo Finance, MarketWatch & more</p>
                  <p className="text-sm text-zinc-500 mb-6">Unlock with Pro Ecopreneur subscription</p>
                  <Button 
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                  >
                    <Unlock className="w-4 h-4 mr-2" /> Why Pro Ecopreneur?
                  </Button>
                </div>
              ) : (
                /* Pro Users - Full News Access */
                <>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                      <Newspaper className="w-6 h-6 text-amber-500" />
                      MARKET NEWS FEED
                    </h2>
                    <Button 
                      onClick={fetchNews}
                      variant="outline"
                      size="sm"
                      disabled={loadingNews}
                      className="border-zinc-700"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingNews ? 'animate-spin' : ''}`} />
                      Refresh News
                    </Button>
                  </div>
                  
                  <p className="text-sm text-zinc-500">
                    Real-time market news from Yahoo Finance, MarketWatch, and more. Updated every 10 minutes.
                  </p>

                  {loadingNews ? (
                    <div className="space-y-4">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="bg-[#111111] border border-zinc-800 p-4 animate-pulse">
                          <div className="flex gap-3">
                            <div className="w-10 h-10 bg-zinc-800 rounded"></div>
                            <div className="flex-1">
                              <div className="h-4 bg-zinc-800 rounded w-3/4 mb-2"></div>
                              <div className="h-3 bg-zinc-800 rounded w-full mb-2"></div>
                              <div className="h-3 bg-zinc-800 rounded w-1/4"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : news.length > 0 ? (
                    <div className="grid gap-4">
                      {news.map((article) => (
                        <NewsItem key={article.id} article={article} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-[#111111] border border-zinc-800">
                      <Newspaper className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                      <p className="text-zinc-400">No news available at the moment</p>
                      <p className="text-sm text-zinc-600">Check back in a few minutes</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === "trending" && (
            <div className="space-y-6">
              {!isPro ? (
                /* Locked Trending for Free Users */
                <div 
                  className="text-center py-16 bg-[#111111] border border-amber-500/30 cursor-pointer hover:bg-amber-500/5 transition-colors"
                  onClick={() => showUpgradePopup("Weekly AI Trending Picks")}
                >
                  <Lock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    WEEKLY TRENDING AI PICKS
                  </h2>
                  <p className="text-zinc-400 mb-2">AI-powered analysis of trending stocks, crypto & commodities</p>
                  <p className="text-sm text-zinc-500 mb-6">Unlock with Pro Ecopreneur subscription</p>
                  <Button 
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                  >
                    <Unlock className="w-4 h-4 mr-2" /> Why Pro Ecopreneur?
                  </Button>
                </div>
              ) : (
                /* Pro Users - Full Trending Access */
                <>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                      <Zap className="w-6 h-6 text-amber-500" />
                      🔥 MEGA WEEKLY TRENDING
                    </h2>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Updates Monday 10AM EST
                      </span>
                      <Button 
                        onClick={fetchWeeklyTrending}
                        disabled={loadingTrending}
                        className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                        size="sm"
                        data-testid="generate-trending-btn"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${loadingTrending ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>

              {weeklyTrending?.success ? (
                <div className="bg-[#111111] border border-amber-500/30 p-6">
                  {/* Header with date */}
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-zinc-800">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-amber-500/20 flex items-center justify-center">
                        <Flame className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <p className="font-bold text-amber-500">MEGA Weekly Report</p>
                        <p className="text-xs text-zinc-500">Week of {weeklyTrending.week}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <AccuracyBadge generatedAt={weeklyTrending.generated_at} accuracy={83} />
                      <p className="text-xs text-emerald-500 mt-1">Next: {weeklyTrending.next_update}</p>
                    </div>
                  </div>
                  
                  {/* Best Opportunity Chart */}
                  {weeklyTrending.best_opportunity?.chart_symbol && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-amber-500/5 to-purple-500/5 border border-amber-500/20">
                      <p className="text-xs text-amber-500 font-bold uppercase mb-2">🏆 Best Opportunity Chart</p>
                      <p className="text-lg font-bold text-white mb-2">{weeklyTrending.best_opportunity.symbol}</p>
                      <img 
                        src={`https://chart.io/${weeklyTrending.best_opportunity.chart_symbol}.png?p=1m&s=l`} 
                        alt={`${weeklyTrending.best_opportunity.symbol} chart`}
                        className="w-full h-48 object-cover rounded opacity-80"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                  
                  {/* Analysis Content */}
                  <div className="prose prose-invert max-w-none">
                    <div className="text-zinc-300 whitespace-pre-wrap leading-relaxed text-sm">
                      {weeklyTrending.analysis}
                    </div>
                  </div>
                  
                  {/* Market Snapshot */}
                  {weeklyTrending.market_snapshot && (
                    <div className="mt-6 pt-6 border-t border-zinc-800">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">📊 Market Snapshot</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-amber-500 font-bold mb-2">Top Stocks</p>
                          {weeklyTrending.market_snapshot.top_stocks?.slice(0, 3).map(s => (
                            <div key={s.symbol} className="text-sm mb-1">
                              <span className="text-zinc-400">{s.symbol}:</span>
                              <span className="text-white ml-2">${s.price}</span>
                              <span className={`ml-2 text-xs ${s.change_1m >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                {s.change_1m >= 0 ? '+' : ''}{s.change_1m}%
                              </span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <p className="text-xs text-purple-500 font-bold mb-2">Top Crypto</p>
                          {weeklyTrending.market_snapshot.top_crypto?.slice(0, 3).map(c => (
                            <div key={c.symbol} className="text-sm mb-1">
                              <span className="text-zinc-400">{c.symbol}:</span>
                              <span className="text-white ml-2">${c.price?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <p className="text-xs text-yellow-500 font-bold mb-2">Commodities</p>
                          {weeklyTrending.market_snapshot.commodities?.slice(0, 3).map(c => (
                            <div key={c.symbol} className="text-sm mb-1">
                              <span className="text-zinc-400">{c.name}:</span>
                              <span className="text-white ml-2">${c.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-zinc-600 mt-6 text-center">
                    ⚠️ This is a TIME-SAVING DATA SERVICE for EDUCATIONAL purposes only. NOT financial advice.
                  </p>
                </div>
              ) : (
                <div className="text-center py-12 bg-[#111111] border border-zinc-800">
                  <Calendar className="w-12 h-12 text-amber-500/50 mx-auto mb-4" />
                  <p className="text-zinc-400 mb-2 font-bold">MEGA Weekly Report</p>
                  <p className="text-sm text-zinc-600 mb-4">
                    {weeklyTrending?.message || "Report updates every Monday at 10:00 AM EST"}
                  </p>
                </div>
              )}
                </>
              )}
            </div>
          )}

          {/* 🏈 SPORTS AI TAB - PRO ECOPRENEUR ONLY */}
          {activeTab === "sports" && (
            <div className="space-y-6">
              {/* PRO Check */}
              {!isPro ? (
                <div className="text-center py-16 bg-[#111111] border border-amber-500/30 rounded-lg">
                  <Lock className="w-16 h-16 text-amber-500/50 mx-auto mb-6" />
                  <h2 className="text-2xl font-bold text-white mb-3">LIONAI Sports</h2>
                  <p className="text-zinc-400 mb-2">AI-Powered Sports Predictions</p>
                  <p className="text-sm text-zinc-500 mb-6 max-w-md mx-auto">
                    Get weekly AI picks for NFL, NBA, NHL & more. Updated every Monday at 8:00 AM EST.
                  </p>
                  <Button 
                    onClick={() => showUpgradePopup("LIONAI Sports")}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade to Pro Ecopreneur
                  </Button>
                  <p className="text-xs text-zinc-600 mt-4">Part of Pro Ecopreneur Plan</p>
                </div>
              ) : (
              <>
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-500/20 flex items-center justify-center rounded-lg">
                    <Trophy className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                      LIONAI SPORTS
                      <Crown className="w-4 h-4 text-amber-500" />
                    </h2>
                    <p className="text-sm text-zinc-500">AI-powered betting insights • Updates Monday 8 AM EST</p>
                  </div>
                </div>
                <Button 
                  onClick={fetchSportsData}
                  disabled={loadingSports}
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
                  data-testid="refresh-sports-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingSports ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {/* Quick Stats Bar */}
              {sportsReport && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-black/40 border border-emerald-500/20 p-3 text-center">
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Total Games</div>
                    <div className="text-2xl font-bold text-emerald-500">{sportsReport.total_games}</div>
                  </div>
                  <div className="bg-black/40 border border-amber-500/20 p-3 text-center">
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">High Confidence</div>
                    <div className="text-2xl font-bold text-amber-500">{sportsReport.high_confidence_picks}</div>
                  </div>
                  <div className="bg-black/40 border border-purple-500/20 p-3 text-center">
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Market Sentiment</div>
                    <div className="text-sm font-bold text-purple-400">{sportsReport.market_sentiment}</div>
                  </div>
                  <div className="bg-black/40 border border-zinc-700 p-3 text-center">
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Updated</div>
                    <div className="text-sm font-bold text-zinc-400">
                      {sportsReport.generated_at ? new Date(sportsReport.generated_at).toLocaleTimeString() : 'Now'}
                    </div>
                  </div>
                </div>
              )}

              {/* 💰 PUBLIC vs SHARP MONEY - Where Smart Money Is Going */}
              <div className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 border border-blue-500/30 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-blue-400 uppercase flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Public vs Sharp Money
                    <span className="text-[9px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full ml-2">AI TRACKED</span>
                  </h3>
                  <span className="text-[10px] text-zinc-500">Follow the smart money</span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { game: 'Lakers vs Warriors', public: 68, sharp: 32, publicPick: 'Lakers -4.5', sharpPick: 'Warriors +4.5', movement: '-2.5 to -4.5', alert: 'SHARP FADE' },
                    { game: 'Chiefs vs Ravens', public: 72, sharp: 28, publicPick: 'Chiefs -3', sharpPick: 'Ravens +3', movement: '-1 to -3', alert: 'PUBLIC TRAP' },
                    { game: 'Celtics vs Bucks', public: 45, sharp: 55, publicPick: 'Celtics -6', sharpPick: 'Celtics -6', movement: '-5 to -6', alert: 'SHARP STEAM' },
                    { game: 'Yankees vs Dodgers', public: 58, sharp: 42, publicPick: 'Over 8.5', sharpPick: 'Under 8.5', movement: '7.5 to 8.5', alert: 'REVERSE LINE' }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-black/40 border border-zinc-700 p-3 rounded">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-white truncate">{item.game}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                          item.alert === 'SHARP STEAM' ? 'bg-emerald-500/20 text-emerald-400' :
                          item.alert === 'SHARP FADE' ? 'bg-amber-500/20 text-amber-400' :
                          item.alert === 'PUBLIC TRAP' ? 'bg-red-500/20 text-red-400' :
                          'bg-purple-500/20 text-purple-400'
                        }`}>
                          {item.alert}
                        </span>
                      </div>
                      
                      {/* Public vs Sharp Bar */}
                      <div className="mb-2">
                        <div className="flex justify-between text-[10px] mb-1">
                          <span className="text-blue-400">Public {item.public}%</span>
                          <span className="text-pink-400">Sharp {item.sharp}%</span>
                        </div>
                        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden flex">
                          <div className="bg-blue-500 h-full" style={{ width: `${item.public}%` }}></div>
                          <div className="bg-pink-500 h-full" style={{ width: `${item.sharp}%` }}></div>
                        </div>
                      </div>
                      
                      {/* Picks */}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="bg-blue-500/10 p-1.5 rounded text-center">
                          <span className="text-[9px] text-blue-400 block">PUBLIC</span>
                          <span className="text-[10px] text-white font-bold">{item.publicPick}</span>
                        </div>
                        <div className="bg-pink-500/10 p-1.5 rounded text-center">
                          <span className="text-[9px] text-pink-400 block">SHARP</span>
                          <span className="text-[10px] text-white font-bold">{item.sharpPick}</span>
                        </div>
                      </div>
                      
                      {/* Line Movement */}
                      <div className="flex items-center justify-center gap-1 text-[10px] text-zinc-500">
                        <TrendingUp className="w-3 h-3" />
                        Line: {item.movement}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Legend */}
                <div className="flex flex-wrap items-center justify-center gap-4 mt-3 pt-3 border-t border-zinc-800">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    <span className="text-[10px] text-zinc-500">SHARP STEAM = Follow sharps</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                    <span className="text-[10px] text-zinc-500">SHARP FADE = Sharps fading public</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    <span className="text-[10px] text-zinc-500">PUBLIC TRAP = Avoid public side</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    <span className="text-[10px] text-zinc-500">REVERSE LINE = Line moved opposite</span>
                  </div>
                </div>
              </div>

              {/* Best Value Picks - Only LIVE and UPCOMING, no FINAL */}
              {sportsReport?.best_value_picks?.length > 0 && (
                <div className="bg-gradient-to-r from-emerald-500/10 to-amber-500/10 border border-emerald-500/30 p-4">
                  <h3 className="text-sm font-bold text-emerald-500 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    TOP AI PICKS
                    <span className="text-[10px] text-zinc-500 ml-2">LIVE & UPCOMING ONLY</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {sportsReport.best_value_picks
                      .filter(pick => pick.status !== 'FINAL' && pick.status !== 'Final' && !pick.is_final)
                      .slice(0, 3)
                      .map((pick, idx) => (
                      <div key={idx} className="bg-black/50 border border-emerald-500/20 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-emerald-500 font-bold">#{idx + 1} PICK</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            pick.confidence === 'HIGH' ? 'bg-emerald-500/20 text-emerald-400' :
                            pick.confidence === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-zinc-700 text-zinc-400'
                          }`}>
                            {pick.confidence}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-400 mb-1">{pick.game}</p>
                        <p className="text-lg font-bold text-white">{pick.pick}</p>
                        <p className="text-xs text-emerald-400 mt-1">{pick.probability?.toFixed(1)}% win prob</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 🔥 AI ULTRA ACCURATE PREDICTIONS - NEW SECTION */}
              <div className="bg-gradient-to-br from-purple-900/20 via-emerald-900/10 to-amber-900/20 border-2 border-emerald-500/40 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-amber-500 rounded-xl flex items-center justify-center animate-pulse">
                        <Sparkles className="w-7 h-7 text-black" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-bounce">
                        AI
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-amber-400 to-purple-400" style={{ fontFamily: 'Barlow Condensed' }}>
                        🎯 AI ULTRA ACCURATE PREDICTIONS
                      </h3>
                      <p className="text-xs text-zinc-400">Daily high-confidence picks • Updated every 6 hours</p>
                    </div>
                  </div>
                  <Button 
                    onClick={fetchAiPredictions}
                    disabled={loadingPredictions}
                    size="sm"
                    className="bg-gradient-to-r from-emerald-500 to-amber-500 hover:from-emerald-600 hover:to-amber-600 text-black font-bold"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${loadingPredictions ? 'animate-spin' : ''}`} />
                    Update
                  </Button>
                </div>

                {/* Summary Stats */}
                {aiPredictionsSummary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    <div className="bg-black/60 border border-emerald-500/30 rounded-lg p-3 text-center">
                      <div className="text-3xl font-black text-emerald-400">{aiPredictionsSummary.ultra_hot_picks || 0}</div>
                      <div className="text-[10px] text-emerald-300 font-bold">🔥 ULTRA HOT</div>
                    </div>
                    <div className="bg-black/60 border border-amber-500/30 rounded-lg p-3 text-center">
                      <div className="text-3xl font-black text-amber-400">{aiPredictionsSummary.high_confidence_picks || 0}</div>
                      <div className="text-[10px] text-amber-300 font-bold">⚡ HIGH CONF</div>
                    </div>
                    <div className="bg-black/60 border border-purple-500/30 rounded-lg p-3 text-center">
                      <div className="text-3xl font-black text-purple-400">{aiPredictionsSummary.total_picks || 0}</div>
                      <div className="text-[10px] text-purple-300 font-bold">📊 TOTAL PICKS</div>
                    </div>
                    <div className="bg-black/60 border border-blue-500/30 rounded-lg p-3 text-center">
                      <div className="text-3xl font-black text-blue-400">{aiPredictionsSummary.avg_confidence || 0}%</div>
                      <div className="text-[10px] text-blue-300 font-bold">📈 AVG CONF</div>
                    </div>
                  </div>
                )}

                {/* Predictions Cards */}
                {loadingPredictions ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="bg-black/40 border border-zinc-700 rounded-xl p-4 animate-pulse">
                        <div className="h-6 bg-zinc-800 rounded w-24 mb-3"></div>
                        <div className="h-8 bg-zinc-800 rounded w-full mb-2"></div>
                        <div className="h-4 bg-zinc-800 rounded w-3/4"></div>
                      </div>
                    ))}
                  </div>
                ) : aiPredictions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Filter out FINAL games - only show LIVE and UPCOMING */}
                    {aiPredictions
                      .filter(pred => pred.status !== 'FINAL' && pred.status !== 'Final' && !pred.is_final)
                      .slice(0, 9)
                      .map((pred, idx) => (
                      <div 
                        key={idx} 
                        className={`relative bg-black/60 border-2 rounded-xl p-4 transition-all hover:scale-[1.02] hover:shadow-lg ${
                          pred.pick_color === 'emerald' ? 'border-emerald-500/50 hover:border-emerald-400 shadow-emerald-500/20' :
                          pred.pick_color === 'amber' ? 'border-amber-500/50 hover:border-amber-400 shadow-amber-500/20' :
                          pred.pick_color === 'blue' ? 'border-blue-500/50 hover:border-blue-400 shadow-blue-500/20' :
                          'border-purple-500/50 hover:border-purple-400 shadow-purple-500/20'
                        }`}
                        data-testid={`ai-prediction-${idx}`}
                      >
                        {/* Rank Badge */}
                        <div className={`absolute -top-2 -left-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-black ${
                          idx === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600' :
                          idx === 1 ? 'bg-gradient-to-br from-zinc-300 to-zinc-500' :
                          idx === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-800' :
                          'bg-zinc-700 text-white'
                        }`}>
                          #{idx + 1}
                        </div>

                        {/* Sport & Quality Badge */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{pred.sport_icon}</span>
                            <span className="text-xs text-zinc-400">{pred.sport}</span>
                          </div>
                          <span className={`text-[10px] px-2 py-1 rounded-full font-bold ${
                            pred.pick_color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
                            pred.pick_color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                            pred.pick_color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>
                            {pred.pick_quality}
                          </span>
                        </div>

                        {/* Teams */}
                        <div className="mb-3">
                          <p className="text-xs text-zinc-500">{pred.away_team} {pred.away_record && `(${pred.away_record})`}</p>
                          <p className="text-sm text-white font-bold">@ {pred.home_team} {pred.home_record && `(${pred.home_record})`}</p>
                        </div>

                        {/* AI Pick with Big Percentage */}
                        <div className={`bg-gradient-to-r rounded-lg p-3 mb-3 ${
                          pred.pick_color === 'emerald' ? 'from-emerald-500/20 to-emerald-600/10' :
                          pred.pick_color === 'amber' ? 'from-amber-500/20 to-amber-600/10' :
                          pred.pick_color === 'blue' ? 'from-blue-500/20 to-blue-600/10' :
                          'from-purple-500/20 to-purple-600/10'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-[10px] text-zinc-400 mb-1">AI PREDICTION</p>
                              <p className="text-lg font-black text-white">{pred.ai_pick}</p>
                            </div>
                            <div className="text-right">
                              <div className={`text-3xl font-black ${
                                pred.pick_color === 'emerald' ? 'text-emerald-400' :
                                pred.pick_color === 'amber' ? 'text-amber-400' :
                                pred.pick_color === 'blue' ? 'text-blue-400' :
                                'text-purple-400'
                              }`}>
                                {pred.ai_confidence}%
                              </div>
                              <p className="text-[10px] text-zinc-500">CONFIDENCE</p>
                            </div>
                          </div>
                        </div>

                        {/* Win Probabilities Bar */}
                        <div className="mb-3">
                          <div className="flex justify-between text-[10px] text-zinc-400 mb-1">
                            <span>Away: {pred.away_win_prob}%</span>
                            <span>Home: {pred.home_win_prob}%</span>
                          </div>
                          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden flex">
                            <div 
                              className="bg-gradient-to-r from-red-500 to-red-400 h-full"
                              style={{ width: `${pred.away_win_prob}%` }}
                            ></div>
                            <div 
                              className="bg-gradient-to-r from-emerald-400 to-emerald-500 h-full"
                              style={{ width: `${pred.home_win_prob}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Game Time & Risk */}
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-zinc-500">
                            {pred.game_time ? new Date(pred.game_time).toLocaleDateString() : 'TBD'} • {pred.game_time ? new Date(pred.game_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                          </span>
                          <span className={`px-2 py-0.5 rounded ${
                            pred.risk_level === 'LOW' ? 'bg-emerald-500/20 text-emerald-400' :
                            pred.risk_level === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {pred.risk_level} RISK
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-black/40 rounded-xl border border-zinc-700">
                    <Sparkles className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400 mb-3">Loading AI predictions...</p>
                    <Button 
                      onClick={fetchAiPredictions}
                      className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
                    >
                      Get AI Picks
                    </Button>
                  </div>
                )}

                {/* Disclaimer */}
                <div className="mt-4 text-center">
                  <p className="text-[10px] text-zinc-600">
                    🤖 Predictions generated by LIONAI advanced algorithms • Updated every 6 hours
                  </p>
                </div>
              </div>

              {/* 🔴 LIVE & UPCOMING GAMES ONLY - No Past Games */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    Live & Upcoming Games
                  </h3>
                  <span className="text-[10px] text-zinc-600">Past games → Final Results below</span>
                </div>
                
                {loadingSports ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="bg-[#111111] border border-zinc-800 p-4 animate-pulse">
                        <div className="h-4 bg-zinc-800 rounded w-20 mb-2"></div>
                        <div className="h-6 bg-zinc-800 rounded w-full mb-2"></div>
                        <div className="h-4 bg-zinc-800 rounded w-3/4"></div>
                      </div>
                    ))}
                  </div>
                ) : (() => {
                  // Filter: Only LIVE and UPCOMING games (exclude FINAL)
                  const liveAndUpcoming = sportsGames.filter(game => 
                    game.status === 'LIVE' || 
                    (game.status !== 'FINAL' && game.status !== 'Final' && 
                     game.game_time && new Date(game.game_time) >= new Date(new Date().setHours(0,0,0,0)))
                  );
                  
                  // Sort: LIVE first, then by game time
                  const sortedGames = liveAndUpcoming.sort((a, b) => {
                    if (a.status === 'LIVE' && b.status !== 'LIVE') return -1;
                    if (b.status === 'LIVE' && a.status !== 'LIVE') return 1;
                    return new Date(a.game_time) - new Date(b.game_time);
                  });
                  
                  return sortedGames.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {sortedGames.map((game, idx) => (
                      <div key={idx} className={`bg-[#111111] border ${game.status === 'LIVE' ? 'border-red-500/50' : 'border-zinc-800'} hover:border-emerald-500/30 p-4 transition-colors relative overflow-hidden`} data-testid={`sports-game-${idx}`}>
                        {/* LIVE Badge Glow Effect */}
                        {game.status === 'LIVE' && (
                          <div className="absolute top-0 right-0 w-20 h-20 bg-red-500/20 blur-2xl"></div>
                        )}
                        
                        {/* Sport Badge & Status */}
                        <div className="flex items-center justify-between mb-3 relative z-10">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{game.sport_icon}</span>
                            <span className="text-xs text-zinc-500">{game.sport}</span>
                          </div>
                          {game.status === 'LIVE' ? (
                            <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                              LIVE
                            </span>
                          ) : (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              game.confidence === 'HIGH' ? 'bg-emerald-500/20 text-emerald-400' :
                              game.confidence === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-zinc-700 text-zinc-400'
                            }`}>
                              {game.confidence || 'UPCOMING'}
                            </span>
                          )}
                        </div>
                        
                        {/* Teams with Records & Score */}
                        <div className="flex items-center justify-between mb-3 relative z-10">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-zinc-400">{game.away_team}</p>
                              {game.away_record && <span className="text-[10px] text-zinc-600">({game.away_record})</span>}
                              {game.status === 'LIVE' && game.away_score !== null && (
                                <span className="text-lg font-bold text-white ml-auto">{game.away_score}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm text-white font-bold">@ {game.home_team}</p>
                              {game.home_record && <span className="text-[10px] text-zinc-600">({game.home_record})</span>}
                              {game.status === 'LIVE' && game.home_score !== null && (
                                <span className="text-lg font-bold text-white ml-auto">{game.home_score}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            {game.status === 'LIVE' ? (
                              <p className="text-xs text-red-400 font-bold">{game.status_detail || 'In Progress'}</p>
                            ) : (
                              <>
                                <p className="text-xs text-zinc-500">
                                  {game.game_time ? new Date(game.game_time).toLocaleDateString() : 'TBD'}
                                </p>
                                <p className="text-xs text-zinc-600">
                                  {game.game_time ? new Date(game.game_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Odds Row */}
                        <div className="grid grid-cols-4 gap-2 mb-3 text-center relative z-10">
                          <div className="bg-black/40 p-2 rounded">
                            <p className="text-[10px] text-zinc-500">AWAY</p>
                            <p className="text-sm font-bold text-white">{game.away_odds > 0 ? `+${game.away_odds}` : game.away_odds}</p>
                          </div>
                          <div className="bg-black/40 p-2 rounded">
                            <p className="text-[10px] text-zinc-500">HOME</p>
                            <p className="text-sm font-bold text-white">{game.home_odds > 0 ? `+${game.home_odds}` : game.home_odds}</p>
                          </div>
                          <div className="bg-black/40 p-2 rounded">
                            <p className="text-[10px] text-zinc-500">SPREAD</p>
                            <p className="text-sm font-bold text-amber-500">{game.spread > 0 ? `+${game.spread}` : game.spread}</p>
                          </div>
                          <div className="bg-black/40 p-2 rounded">
                            <p className="text-[10px] text-zinc-500">WIN %</p>
                            <p className="text-sm font-bold text-purple-400">{game.home_win_prob}%</p>
                          </div>
                        </div>
                        
                        {/* AI Prediction */}
                        <div className="bg-emerald-500/10 border border-emerald-500/20 p-2 rounded relative z-10">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-3 h-3 text-emerald-500" />
                            <span className="text-[10px] text-emerald-500 font-bold uppercase">AI Pick</span>
                          </div>
                          <p className="text-sm text-white font-bold mt-1">{game.prediction?.pick}</p>
                          <p className="text-xs text-zinc-400">{game.prediction?.reason} • {game.prediction?.probability?.toFixed(1)}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-[#111111] border border-zinc-800 rounded">
                    <Trophy className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">No live or upcoming games at the moment</p>
                    <p className="text-xs text-zinc-600 mt-2">Check Final Results below for completed games</p>
                  </div>
                );
                })()}
              </div>

              {/* 🎯 AI SPORTS PICKS - Daily/Weekly/Monthly */}
              <div className="bg-gradient-to-r from-emerald-500/5 via-amber-500/5 to-emerald-500/5 border-2 border-emerald-500/40 p-6 rounded-xl mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/30 to-amber-500/30 rounded-full flex items-center justify-center">
                      <Target className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-amber-400" style={{ fontFamily: 'Barlow Condensed' }}>
                        🎯 AI SPORTS PICKS
                        <span className="ml-2 text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded animate-pulse">LIONAI™</span>
                      </h3>
                      <p className="text-xs text-zinc-500">Daily • Weekly • Monthly picks for Pro Ecopreneurs</p>
                    </div>
                  </div>
                  <Button 
                    onClick={fetchSportsAiPicks}
                    disabled={loadingSportsAiPicks}
                    size="sm"
                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400"
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${loadingSportsAiPicks ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                
                {loadingSportsAiPicks ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="bg-black/50 border border-emerald-500/20 p-5 animate-pulse rounded">
                        <div className="h-4 bg-zinc-800 rounded w-24 mb-3"></div>
                        <div className="h-6 bg-zinc-800 rounded w-32 mb-2"></div>
                        <div className="h-3 bg-zinc-800 rounded w-full"></div>
                      </div>
                    ))}
                  </div>
                ) : sportsAiPicks?.picks ? (
                  <div className="space-y-6">
                    {/* Daily Pick */}
                    {sportsAiPicks.picks.daily_pick && (
                      <div className="bg-gradient-to-r from-emerald-500/10 to-amber-500/10 border-2 border-emerald-500/40 p-5 rounded-lg relative overflow-hidden">
                        <div className="absolute top-0 right-0 px-3 py-1 bg-emerald-500 text-black text-xs font-bold">
                          DAILY PICK
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/30 to-amber-500/30 rounded-full flex items-center justify-center text-2xl">
                              {sportsAiPicks.picks.daily_pick.sport_icon || "🏆"}
                            </div>
                            <div>
                              <div className="text-sm text-zinc-400">{sportsAiPicks.picks.daily_pick.sport}</div>
                              <div className="text-lg font-bold text-white">{sportsAiPicks.picks.daily_pick.matchup}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-emerald-400">{sportsAiPicks.picks.daily_pick.pick}</div>
                            <div className="text-xs text-zinc-500">{sportsAiPicks.picks.daily_pick.confidence}% confidence</div>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-emerald-500/20">
                          <p className="text-sm text-zinc-300">{sportsAiPicks.picks.daily_pick.reason}</p>
                          <div className="flex items-center gap-3 mt-3 flex-wrap">
                            <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-400 rounded">
                              💡 {sportsAiPicks.picks.daily_pick.odds_tip}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              sportsAiPicks.picks.daily_pick.risk === 'LOW' ? 'bg-emerald-500/20 text-emerald-400' :
                              sportsAiPicks.picks.daily_pick.risk === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                              'bg-amber-500/20 text-amber-400'
                            }`}>
                              {sportsAiPicks.picks.daily_pick.risk} Risk
                            </span>
                            <span className="text-xs text-zinc-600">⏰ {sportsAiPicks.picks.daily_pick.game_time}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Weekly & Monthly Picks */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Weekly Picks */}
                      {sportsAiPicks.picks.weekly_picks && sportsAiPicks.picks.weekly_picks.length > 0 && (
                        <div className="bg-black/50 border border-blue-500/30 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-bold text-blue-400 flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              WEEKLY PICKS
                            </span>
                            <span className="text-xs text-zinc-600">Updates: {sportsAiPicks.next_updates?.weekly}</span>
                          </div>
                          <div className="space-y-3">
                            {sportsAiPicks.picks.weekly_picks.map((pick, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-blue-500/5 p-3 rounded">
                                <div className="flex items-center gap-3">
                                  <span className="text-xl">{pick.sport_icon}</span>
                                  <div>
                                    <div className="text-xs text-zinc-500">{pick.sport}</div>
                                    <div className="text-sm font-bold text-white">{pick.matchup}</div>
                                    <div className="text-xs text-blue-400">{pick.pick}</div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-bold text-emerald-400">{pick.confidence}%</div>
                                  <div className={`text-xs ${
                                    pick.risk === 'LOW' ? 'text-emerald-400' : 
                                    pick.risk === 'HIGH' ? 'text-red-400' : 'text-amber-400'
                                  }`}>{pick.risk}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Monthly Picks */}
                      {sportsAiPicks.picks.monthly_picks && sportsAiPicks.picks.monthly_picks.length > 0 && (
                        <div className="bg-black/50 border border-purple-500/30 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-bold text-purple-400 flex items-center gap-2">
                              <Trophy className="w-4 h-4" />
                              MONTHLY PICKS
                            </span>
                            <span className="text-xs text-zinc-600">Updates: {sportsAiPicks.next_updates?.monthly}</span>
                          </div>
                          <div className="space-y-3">
                            {sportsAiPicks.picks.monthly_picks.map((pick, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-purple-500/5 p-3 rounded">
                                <div className="flex items-center gap-3">
                                  <span className="text-xl">{pick.sport_icon}</span>
                                  <div>
                                    <div className="text-xs text-zinc-500">{pick.sport} • {pick.type}</div>
                                    <div className="text-sm font-bold text-white">{pick.pick}</div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-bold text-purple-400">{pick.confidence}%</div>
                                  <div className="text-xs text-emerald-400">{pick.potential}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-[9px] text-zinc-600 text-center">
                      {sportsAiPicks.picks.ai_generated ? '🤖 Generated by LIONAI using Gemini 3 Flash' : '📊 Rule-based analysis'} • 
                      {sportsAiPicks.picks.disclaimer || 'For educational purposes only. Not betting advice.'} • 
                      Same picks for all Pro Ecopreneurs
                    </p>
                  </div>
                ) : (
                  <div className="bg-black/40 border border-emerald-500/20 p-6 text-center rounded">
                    <RefreshCw className="w-8 h-8 mx-auto text-emerald-500/50 mb-2 animate-spin" />
                    <p className="text-sm text-zinc-500">Loading AI picks...</p>
                  </div>
                )}
              </div>

              {/* 📊 SPORTS RESULTS TABLE - Last 7 Days */}
              <div className="bg-[#0a0a0a] border border-zinc-800 p-6 rounded-xl mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center">
                      <LineChart className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                        📊 FINAL RESULTS
                      </h3>
                      <p className="text-xs text-zinc-500">Last 7 days • Updated daily • All sports</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sportsResults?.sports_count && (
                      <div className="flex gap-1">
                        {Object.entries(sportsResults.sports_count).slice(0, 4).map(([sport, count]) => (
                          <span key={sport} className="text-xs px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded">
                            {sport}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                    <Button 
                      onClick={fetchSportsResults}
                      disabled={loadingSportsResults}
                      size="sm"
                      variant="ghost"
                      className="text-zinc-400 hover:text-white"
                    >
                      <RefreshCw className={`w-3 h-3 ${loadingSportsResults ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
                
                {loadingSportsResults ? (
                  <div className="animate-pulse space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-10 bg-zinc-900 rounded"></div>
                    ))}
                  </div>
                ) : sportsResults?.results && sportsResults.results.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800 text-zinc-500 text-xs">
                          <th className="py-2 px-2 text-left">SPORT</th>
                          <th className="py-2 px-2 text-left">DATE</th>
                          <th className="py-2 px-2 text-left">AWAY</th>
                          <th className="py-2 px-1 text-center">SCORE</th>
                          <th className="py-2 px-2 text-left">HOME</th>
                          <th className="py-2 px-2 text-center">WINNER</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sportsResults.results.slice(0, 20).map((result, idx) => (
                          <tr key={result.id || idx} className="border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors">
                            <td className="py-2 px-2">
                              <span className="flex items-center gap-1">
                                <span>{result.sport_icon}</span>
                                <span className="text-xs text-zinc-400 hidden sm:inline">{result.sport}</span>
                              </span>
                            </td>
                            <td className="py-2 px-2 text-xs text-zinc-500">{result.date}</td>
                            <td className="py-2 px-2 text-white font-medium">
                              <span className={result.winner === result.away_team ? 'text-emerald-400 font-bold' : ''}>
                                {result.away_team}
                              </span>
                            </td>
                            <td className="py-2 px-1 text-center">
                              <span className="text-white font-bold">{result.away_score}</span>
                              <span className="text-zinc-600 mx-1">-</span>
                              <span className="text-white font-bold">{result.home_score}</span>
                            </td>
                            <td className="py-2 px-2 text-white font-medium">
                              <span className={result.winner === result.home_team ? 'text-emerald-400 font-bold' : ''}>
                                {result.home_team}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">
                                {result.winner}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {sportsResults.results.length > 20 && (
                      <p className="text-xs text-zinc-600 text-center mt-3">
                        Showing 20 of {sportsResults.results.length} results
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <LineChart className="w-10 h-10 mx-auto text-zinc-700 mb-2" />
                    <p className="text-sm text-zinc-500">No results available</p>
                    <Button onClick={fetchSportsResults} className="mt-3" size="sm" variant="outline">
                      Load Results
                    </Button>
                  </div>
                )}
                
                <p className="text-[9px] text-zinc-600 text-center mt-4">
                  Data from ESPN • Updates daily • {sportsResults?.last_updated || 'Real-time'}
                </p>
              </div>

              {/* Disclaimer */}
              <div className="text-center p-4 bg-zinc-900/50 border border-zinc-800 rounded">
                <p className="text-xs text-zinc-500">
                  ⚠️ For entertainment & educational purposes only. NOT gambling advice. 
                  Odds and predictions are based on public data and AI analysis.
                  Always gamble responsibly.
                </p>
              </div>
              </>
              )}
            </div>
          )}
        </div>
        
        {/* AI HOT PICKS - LIVE PRICES - Updated Daily at 9AM */}
        <div className="mt-8 pb-6" data-testid="hot-picks-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Zap className="w-5 h-5 text-amber-500 animate-pulse" />
              TODAY'S HOT PICKS
              <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded animate-pulse">LIVE</span>
              <span className="text-[10px] px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded ml-1">AI-CURATED 9AM EST</span>
            </h3>
            <div className="flex items-center gap-4">
              <span className="text-xs text-zinc-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Next refresh: {Math.floor(nextRefreshIn / 60)}:{(nextRefreshIn % 60).toString().padStart(2, '0')}
              </span>
              <Button 
                onClick={() => { handleRefresh(); fetchHotPicks(); }}
                disabled={refreshing}
                className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-500 text-xs h-7 px-3"
                size="sm"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* TradingView Ticker Tape Widget */}
          <div className="bg-[#0a0a0a] border border-zinc-800 p-1 mb-4 overflow-hidden rounded">
            <div 
              id="tradingview-ticker-tape"
              ref={(el) => {
                if (el && !el.dataset.loaded) {
                  el.dataset.loaded = "true";
                  const container = document.createElement("div");
                  container.className = "tradingview-widget-container";
                  container.style.width = "100%";
                  const widgetDiv = document.createElement("div");
                  widgetDiv.className = "tradingview-widget-container__widget";
                  container.appendChild(widgetDiv);
                  const script = document.createElement("script");
                  script.src = "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js";
                  script.async = true;
                  script.textContent = JSON.stringify({
                    symbols: [
                      { proName: "FOREXCOM:SPXUSD", title: "S&P 500" },
                      { proName: "FOREXCOM:NSXUSD", title: "NASDAQ" },
                      { proName: "BITSTAMP:BTCUSD", title: "Bitcoin" },
                      { proName: "BITSTAMP:ETHUSD", title: "Ethereum" },
                      { proName: "OANDA:XAUUSD", title: "Gold" },
                      { proName: "NASDAQ:AAPL", title: "Apple" },
                      { proName: "NASDAQ:NVDA", title: "NVIDIA" },
                      { proName: "NASDAQ:MSFT", title: "Microsoft" },
                      { proName: "NASDAQ:GOOGL", title: "Google" },
                      { proName: "NASDAQ:AMZN", title: "Amazon" },
                      { proName: "NASDAQ:TSLA", title: "Tesla" },
                      { proName: "NYSE:GS", title: "Goldman" },
                      { proName: "CBOE:VIX", title: "VIX" },
                      { proName: "TVC:DXY", title: "Dollar" }
                    ],
                    showSymbolLogo: true,
                    isTransparent: true,
                    displayMode: "adaptive",
                    colorTheme: "dark",
                    locale: "en"
                  });
                  container.appendChild(script);
                  el.appendChild(container);
                }
              }}
            />
          </div>

          {/* Dynamic Hot Picks Grid - 5x2 */}
          {hotPicks?.picks ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {hotPicks.picks.map((pick, idx) => (
                  <div
                    key={`${pick.symbol}-${idx}`}
                    data-testid={`hot-pick-${pick.symbol}`}
                    onClick={() => setSelectedHotPick(selectedHotPick?.symbol === pick.symbol ? null : pick)}
                    className={`bg-[#0a0a0a] border overflow-hidden rounded cursor-pointer transition-all hover:scale-[1.02] ${
                      selectedHotPick?.symbol === pick.symbol ? 'border-amber-500 ring-1 ring-amber-500/30' : 'border-zinc-800 hover:border-zinc-600'
                    }`}
                  >
                    {/* Header with signal badge */}
                    <div className="flex items-center justify-between p-2 border-b border-zinc-800/50">
                      <div className="flex items-center gap-1.5">
                        {pick.type === 'crypto' ? (
                          <Bitcoin className="w-3.5 h-3.5" style={{ color: pick.color }} />
                        ) : pick.type === 'commodity' ? (
                          <Gem className="w-3.5 h-3.5" style={{ color: pick.color }} />
                        ) : (
                          <TrendingUp className="w-3.5 h-3.5" style={{ color: pick.color }} />
                        )}
                        <span className="text-xs font-bold" style={{ color: pick.color }}>{pick.name}</span>
                      </div>
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded ${
                        pick.signal?.includes('STRONG BUY') ? 'bg-emerald-500/30 text-emerald-300' :
                        pick.signal?.includes('BUY') ? 'bg-emerald-500/20 text-emerald-400' :
                        pick.signal?.includes('SELL') ? 'bg-red-500/20 text-red-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {pick.signal}
                      </span>
                    </div>
                    
                    {/* TradingView Mini Chart - Lazy loaded */}
                    <div style={{ height: '120px', pointerEvents: 'none' }}>
                      <LazyIframe
                        src={`https://s.tradingview.com/embed-widget/mini-symbol-overview/?locale=en#${encodeURIComponent(JSON.stringify({
                          symbol: pick.tv_symbol,
                          width: "100%",
                          height: "100%",
                          dateRange: "1D",
                          colorTheme: "dark",
                          isTransparent: true,
                          autosize: true,
                          largeChartUrl: "",
                          chartOnly: false,
                          noTimeScale: false
                        }))}`}
                        style={{ width: '100%', height: '100%' }}
                        title={pick.symbol}
                      />
                    </div>

                    {/* Momentum bar */}
                    <div className="px-2 pb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[9px] text-zinc-500">RSI: {pick.rsi}</span>
                        <span className="text-[9px] text-zinc-500">Power: {pick.momentum}%</span>
                      </div>
                      <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            pick.momentum >= 75 ? 'bg-emerald-500' :
                            pick.momentum >= 50 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${pick.momentum}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Click-to-reveal AI Insight Panel */}
              {selectedHotPick && (
                <div
                  data-testid="hot-pick-insight"
                  className="mt-3 bg-gradient-to-r from-amber-500/5 via-transparent to-amber-500/5 border border-amber-500/20 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-black text-white">{selectedHotPick.symbol}</span>
                        <span className="text-sm text-zinc-400">{selectedHotPick.name}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                          selectedHotPick.signal?.includes('BUY') ? 'bg-emerald-500/20 text-emerald-400' :
                          selectedHotPick.signal?.includes('SELL') ? 'bg-red-500/20 text-red-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {selectedHotPick.signal}
                        </span>
                      </div>
                      <p className="text-sm text-amber-400 font-medium mb-2">
                        {selectedHotPick.insight}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-zinc-400">
                        <span className={`font-bold ${selectedHotPick.rsi < 30 ? 'text-emerald-400' : selectedHotPick.rsi > 70 ? 'text-red-400' : 'text-zinc-300'}`}>
                          RSI: {selectedHotPick.rsi} {selectedHotPick.rsi < 30 ? '(Oversold)' : selectedHotPick.rsi > 70 ? '(Overbought)' : '(Neutral)'}
                        </span>
                        <span>|</span>
                        <span className={`font-bold ${selectedHotPick.momentum >= 70 ? 'text-emerald-400' : selectedHotPick.momentum >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                          Momentum: {selectedHotPick.momentum}/100
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedHotPick(null)}
                      className="text-zinc-500 hover:text-white ml-4 text-xs"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
              
              <div className="mt-3 flex items-center justify-center gap-4">
                <AccuracyBadge generatedAt={hotPicks?.generated_at} accuracy={86} />
                <span className="text-[10px] text-zinc-600">RSI + MACD + Bollinger Bands + AI</span>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="bg-[#0a0a0a] border border-zinc-800 rounded h-44 animate-pulse" />
              ))}
            </div>
          )}
          
          {/* Link to Markets Page */}
          <div className="mt-4 text-center">
            <Link 
              to="/markets"
              className="inline-flex items-center gap-2 text-sm text-amber-500 hover:text-amber-400 transition-colors"
            >
              View all market prices <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>
      
      {/* Pro Ecopreneur Popup */}
      <ProEcopreneurPopup 
        isOpen={showProPopup} 
        onClose={() => setShowProPopup(false)} 
        featureName={popupFeature}
      />
    </div>
  );
}
